import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, from, of, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError, switchMap, tap, finalize, retry } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { Document, Packer, Paragraph, TextRun, HeadingLevel } from 'docx';
import * as pdfjsLib from 'pdfjs-dist';
import * as mammoth from 'mammoth';
import * as use from '@tensorflow-models/universal-sentence-encoder';
import * as Tesseract from 'tesseract.js';
import { createWorker } from 'tesseract.js';
import { PDFExtract } from 'pdf.js-extract';

// Set the worker source for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export interface DocumentAnalysis {
  type: 'summary' | 'bullet-points' | 'answer';
  content: string;
}

export interface DocumentCharacteristics {
  isProbablyAcademic: boolean;
  hasMathContent: boolean;
  hasCodeBlocks: boolean;
  documentType: string;
  fileName: string;
  pageCount?: number;
  wordCount?: number;
  extractionMethod?: string;
  isScannedDocument?: boolean;
  containsTables?: boolean;
}

// System prompts
@Injectable({
  providedIn: 'root'
})
export class AIService {
  private openRouterApiUrl = 'https://openrouter.ai/api/v1';
  private azureOpenAIUrl: string;
  private apiKey: string = '';
  private headers: HttpHeaders = new HttpHeaders();
  private azureHeaders: HttpHeaders = new HttpHeaders();
  
  // Using Azure OpenAI as primary model
  private modelId = 'o4-mini'; // Azure OpenAI deployment name
  // Fallback models from OpenRouter if primary model fails
  private fallbackModels = [
    'mistralai/mistral-7b-instruct-v0.2',
    'google/gemma-1.1-7b-it', 
    'google/gemini-pro', 
    'anthropic/claude-3-haiku-20240307'
  ];
  private currentModelIndex = 0;
  private useAzure = true; // Flag to track if we're using Azure OpenAI or fallback
  
  // Sentence encoder model for semantic search
  private sentenceEncoder: any = null;
  private isEncoderLoading = false;
  private encoderLoadingSubject = new BehaviorSubject<boolean>(false);
  public encoderLoading$ = this.encoderLoadingSubject.asObservable();

  // Document chunking for improved context handling
  private documentChunks: string[] = [];
  private fullDocumentContent: string = '';
  private chunkIndexMap: Map<string, number> = new Map(); // Maps chunk hash to index
  private chunkOverlapSize: number = 200; // Characters of overlap between chunks

  // Add a cache for document embeddings to avoid recomputing them
  private documentEmbeddingsCache: Map<string, number[]> = new Map();
  private queryEmbeddingsCache: Map<string, number[]> = new Map();

  // System prompts - make it public instead of private
  public systemPromptForGeneral = `You are an educational assistant that helps teachers, instructional designers, and content creators develop high-quality educational materials.

When responding:
1. Be concise but thorough in your explanations
2. Use clean, consistent formatting for all outputs
3. For tables:
   - Use standard markdown format
   - Keep column headers clear and concise
   - Align data properly within columns
   - Only include the table, not the markdown syntax explanation
4. For lists and bullet points:
   - Use consistent formatting
   - Keep items parallel in structure
   - Maintain proper indentation for nested lists
5. Format your response appropriate to the content:
   - Use bullet points for lists
   - Use numbered lists for sequential steps
   - Use bold sparingly, only for key terms that need emphasis
   - Use code blocks for code or technical content
6. Never include unnecessary blank lines or spaces
7. Always use proper Markdown syntax
8. Do not use headings (##) unless specifically requested
9. Keep responses in a natural, conversational tone without excessive formatting

Apply appropriate formatting but focus on making your response clear, useful, and well-structured.`;

  // For document analysis
  public systemPromptForDocument = `You are a document analysis assistant capable of handling both document-specific questions and general knowledge queries.

When answering questions:
1. For document-specific questions: Base your answer solely on the document content provided.
2. For general knowledge questions: Clearly indicate when you're providing information beyond what's in the document.
3. When asked about specific sections: First check if that section exists in the document. If not, inform the user that section isn't clearly labeled.
4. IMPORTANT: Only create tables when explicitly requested by the user (with phrases like "create a table", "show in table form", etc.). Do not create tables by default. When tables are requested, use proper markdown format with headers and aligned columns.
5. CRITICAL: Understand questions that mean the same thing but are phrased differently. For example:
   - "Who is the author?" = "Who wrote this?" = "Who created this document?" = "Presented by?"
   - "What is the main topic?" = "What is this about?" = "Subject of this document?"
   - "When was this published?" = "Publication date?" = "When did this come out?"
6. For document metadata questions:
   - "Presenter" and "author" typically refer to the same person in academic contexts
   - If the document mentions someone "presented by X" or "submitted by X", then X is likely the author
   - Look for names near document titles, in headers, or at the beginning/end of the document
   - Make logical connections between concepts: a person who submits, presents, or prepares a document is usually its author
7. Infer implicit information even when not stated in the exact words of the query.
8. If you don't find the exact phrase the user is asking about, look for equivalent or related information.
9. When asked about document metadata like authors, check for names appearing with terms like "prepared by", "presented by", "submitted by", or other attribution markers.
10. Provide direct, helpful answers and make reasonable inferences based on what's available. If information is truly missing, simply provide the best possible answer without drawing attention to the limitations.
11. When generating tables, summaries, bullet points, or reports, ensure the output is comprehensive and well-structured for easy copying into presentations or documents.
12. Use bold text sparingly, only for key terms that need emphasis.
13. Do not use headings (##) unless specifically requested.
14. Keep responses in a natural, conversational tone without excessive formatting.

When the question is ambiguous, prioritize the document content first. If you're asked to ignore the document content, you may provide general knowledge.

Respond in clear, concise prose unless explicitly requested to use a different format.`;

  constructor(
    private http: HttpClient,
    private ngZone: NgZone
  ) {
    this.setupPdfWorker();
    // Set up Azure OpenAI configuration
    this.azureOpenAIUrl = `${environment.AZURE_OPENAI_ENDPOINT}/openai/deployments/${environment.AZURE_OPENAI_DEPLOYMENT}/chat/completions?api-version=${environment.AZURE_OPENAI_API_VERSION}`;
    
    // Set up Azure headers
    this.azureHeaders = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('api-key', environment.AZURE_OPENAI_API_KEY);
    
    // Set up OpenRouter headers as fallback
    const openRouterApiKey = environment.OPENROUTER_API_KEY;
    this.headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${openRouterApiKey}`,
      'HTTP-Referer': window.location.origin || 'https://www.academic-ai-assistant.com',
      'X-Title': 'AI Academic Assistant'
    });
    
    // Initialize the sentence encoder in the background
    this.loadSentenceEncoder();
    
    // Test the Azure OpenAI connection
    this.testAzureConnection();
  }

  private setupPdfWorker(): void {
    const workerUrl = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
    pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    console.log('[AIService] PDF Worker URL configured:', workerUrl);
  }

  private async loadSentenceEncoder(): Promise<void> {
    if (this.sentenceEncoder || this.isEncoderLoading) return;
    
    try {
      this.isEncoderLoading = true;
      this.encoderLoadingSubject.next(true);
      
      // Load the Universal Sentence Encoder model
      this.sentenceEncoder = await use.load();
      
      console.log('Sentence encoder loaded successfully');
    } catch (error) {
      console.error('Error loading sentence encoder:', error);
    } finally {
      this.isEncoderLoading = false;
      this.encoderLoadingSubject.next(false);
    }
  }

  /**
   * Tests the connection to Azure OpenAI API
   */
  private testAzureConnection(): void {
    const testPayload = {
      messages: [
        { role: 'user', content: 'Hello, are you available?' }
      ]
    };
    
    console.log('Testing Azure OpenAI connection...');
    console.log('Using deployment:', this.modelId);
    console.log('Request URL:', this.azureOpenAIUrl);
    console.log('Request payload:', JSON.stringify(testPayload, null, 2));
    console.log('Request headers:', this.azureHeaders);
    
    this.http.post(this.azureOpenAIUrl, testPayload, { 
      headers: this.azureHeaders,
      observe: 'response'
    })
    .subscribe({
      next: (response) => {
        console.log('Azure OpenAI connection successful!');
        console.log('Status:', response.status);
        console.log('Response headers:', response.headers);
        console.log('Response body:', response.body);
        this.useAzure = true;
      },
      error: (error) => {
        console.error('Azure OpenAI connection failed!');
        console.error('Status code:', error.status);
        console.error('Error message:', error.message);
        console.error('Error details:', error.error);
        
        // If Azure fails, test OpenRouter connection as fallback
        console.log('Falling back to OpenRouter...');
        this.useAzure = false;
        this.testOpenRouterConnection();
      }
    });
  }

  /**
   * Tests the connection to OpenRouter API
   */
  private testOpenRouterConnection(): void {
    const testPayload = {
      model: this.fallbackModels[0],
      messages: [
        { role: 'user', content: 'Hello' }
      ],
      max_tokens: 5,
      temperature: 0.7,
      stream: false
    };
    
    console.log('Testing OpenRouter connection...');
    console.log('Using model:', this.fallbackModels[0]);
    
    this.http.post(`${this.openRouterApiUrl}/chat/completions`, testPayload, { 
      headers: this.headers,
      observe: 'response'
    })
    .subscribe({
      next: (response) => {
        console.log('OpenRouter connection successful!');
        console.log('Status:', response.status);
        console.log('Response headers:', response.headers);
        console.log('Response body:', response.body);
      },
      error: (error) => {
        console.error('OpenRouter connection failed!');
        console.error('Status code:', error.status);
        console.error('Error message:', error.message);
        console.error('Error details:', error.error);
        
        if (error.status === 401 || error.status === 403) {
          console.error('API KEY ISSUE: Please make sure your OpenRouter API key is valid.');
        } else if (error.status === 400) {
          console.error('BAD REQUEST: Check your request format or model ID.');
          if (error.error && error.error.error) {
            console.error('Error details from OpenRouter:', error.error.error);
          }
        }
      }
    });
  }

  /**
   * Tries to generate text with fallback models if the main model fails
   * @param messages Array of message objects with role and content
   * @param modelIndex Index of the model to use (0 for primary, 1+ for fallbacks)
   * @param options Additional options like max_tokens and temperature
   * @returns Observable with the AI response
   */
  generateTextWithFallback(
    messages: {role: string, content: string}[], 
    modelIndex: number = 0,
    options: { max_tokens?: number, temperature?: number } = {}
  ): Observable<string> {
    // If using Azure OpenAI
    if (this.useAzure && modelIndex === 0) {
      const payload = {
        messages: messages
      };
      
      console.log('Azure OpenAI request:', {
        url: this.azureOpenAIUrl,
        headers: this.azureHeaders,
        payload: payload
      });
      
      return this.http.post<any>(this.azureOpenAIUrl, payload, { headers: this.azureHeaders }).pipe(
        map(response => {
          if (!response || !response.choices || !response.choices.length) {
            throw new Error('Invalid response format from Azure API');
          }
          return response.choices[0].message.content.trim();
        }),
        catchError(error => {
          console.error('Error with Azure OpenAI:', error);
          console.error('Full error details:', {
            status: error.status,
            message: error.message,
            error: error.error
          });
          // Fall back to OpenRouter models
          this.useAzure = false;
          return this.generateTextWithFallback(messages, 1, options);
        })
      );
    }
    
    // If using OpenRouter fallback models
    let currentModel = this.fallbackModels[modelIndex - 1];
    if (!currentModel) {
      return throwError(() => new Error('All available models failed'));
    }
    
    console.log(`Using OpenRouter model: ${currentModel}, Index: ${modelIndex}`);
    
    const payload = {
      model: currentModel,
      messages: messages,
      max_tokens: options.max_tokens || 800,
      temperature: options.temperature || 0.7,
      stream: false
    };
    
    return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, payload, { headers: this.headers }).pipe(
      map(response => {
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from API');
        }
        return response.choices[0].message.content.trim();
      }),
      catchError(error => {
        console.error(`Error with model ${currentModel}:`, error);
        // Try next fallback model
        return this.generateTextWithFallback(messages, modelIndex + 1, options);
      })
    );
  }

  private validateAPIKey(): Observable<boolean> {
    // If we don't have a proper API key setup, return an error
    if (!this.headers.get('Authorization')) {
      console.error('No API key provided for OpenRouter');
      return throwError(() => new Error('API key for AI service is not configured. Please check your environment settings.'));
    }
    
    // Create a lightweight API request to validate the key
    const payload = {
      model: this.modelId,
      messages: [{ role: 'user', content: 'Hello' }],
      max_tokens: 1, // Minimize token usage
      stream: false
    };
    
    return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, payload, { headers: this.headers }).pipe(
      map(() => true), // Key is valid
      catchError(error => {
        if (error.status === 401 || error.status === 403) {
          console.error('Invalid API key for OpenRouter service:', error);
          return throwError(() => new Error('Your API key for the AI service is invalid or expired. Please check your configuration.'));
        }
        // Other errors might not be related to auth
        return of(true);
      })
    );
  }

  generateText(input: string | {role: string, content: string}[]): Observable<string> {
    if (typeof input === 'string') {
      // If input is a string, create a simple message
      const payload = {
        model: this.modelId,
        messages: [
          { role: 'user', content: input }
        ],
        temperature: 0.7,
        max_tokens: 1500,
        stream: false
      };

      return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, payload, { headers: this.headers }).pipe(
        map(response => {
          if (!response.choices || !response.choices.length) {
            throw new Error('Invalid API response format');
          }
          return this.formatResponseContent(response.choices[0].message.content);
        }),
          catchError(error => {
          console.error('Error in generateText:', error);
          return of('Sorry, I was unable to generate a response. Please try again or rephrase your question.');
        })
      );
    } else {
      // If input is an array of messages, use them directly
      // Check if the user is asking for a table
      let lastUserMessage = '';
      for (let i = input.length - 1; i >= 0; i--) {
        if (input[i].role === 'user') {
          lastUserMessage = input[i].content;
          break;
        }
      }
      
      const isTableRequest = /table|comparison|grid|matrix|chart/i.test(lastUserMessage);
      
      // Create an enhanced message array with a better system prompt
      const enhancedMessages = [...input];
      
      // Add a system message at the beginning if one doesn't exist
      if (enhancedMessages.length === 0 || enhancedMessages[0].role !== 'system') {
        const systemPrompt = this.systemPromptForGeneral;
        
        enhancedMessages.unshift({
          role: 'system',
          content: systemPrompt
        });
      }
      
      // Use the fallback mechanism with the enhanced messages
      return this.generateTextWithFallback(enhancedMessages, 0, {
        temperature: isTableRequest ? 0.3 : 0.7 // Lower temperature for tables for more consistent formatting
      }).pipe(
        map(response => {
          // For table requests, apply special table formatting
          if (isTableRequest) {
            // Apply the same formatting logic used in document analysis
            return this.formatResponseContent(response);
          }
          
          // For non-table responses, format as usual
          return this.formatResponseContent(response);
        }),
        catchError(error => {
          console.error('All models failed in generateText:', error);
          return of('Sorry, I was unable to generate a response. Please try again.');
        })
      );
    }
  }

  getDocumentSummary(context: string): Observable<DocumentAnalysis> {
    // For summaries, first check if we need to use our chunking approach
    if (context.length > 10000) {
      // For large documents, use the recursive summarization approach
      return this.getRecursiveSummary();
    }

    // For smaller documents, use the traditional approach
    const enhancedMessages = [
        { 
          role: 'system', 
          content: `You are an expert document analyst. Create clear, concise summaries focusing on main ideas and key points only.`
        },
        { 
          role: 'user', 
          content: `Create a concise summary of the following document in 5 clear bullet points. Focus on the main content and key ideas:\n\n${context}`,
        }
    ];
    
    // Use current model (primary or fallback)
    const currentModel = this.currentModelIndex === 0 ? this.modelId : this.fallbackModels[this.currentModelIndex - 1];
    
    return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, {
      model: currentModel,
      messages: enhancedMessages,
      temperature: 0.1, // Low temperature for factual summary
      max_tokens: 500, // Reduced from original
      stream: false
    }, { headers: this.headers }).pipe(
      map(response => {
        // Reset model index if successful
        this.currentModelIndex = 0;
        
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from API');
        }
        
        return {
          type: 'summary' as const,
          content: this.formatResponseContent(response.choices[0].message.content.trim())
        };
      }),
      catchError(error => {
        console.error('Error generating document summary:', error);
        
        // Try fallback model if available
        if (this.currentModelIndex < this.fallbackModels.length) {
          console.log(`Trying fallback model ${this.currentModelIndex + 1} of ${this.fallbackModels.length}`);
          this.currentModelIndex++;
          return this.getDocumentSummary(context);
        }
        
        // Reset model index if all fallbacks failed
        this.currentModelIndex = 0;
        
        throw new Error(`Failed to generate summary: ${error.message || 'Unknown error'}`);
      })
    );
  }

  /**
   * Get a summary of a large document using recursive summarization approach
   * Summarizes each chunk then creates a summary of summaries for better handling of large documents
   */
  private getRecursiveSummary(): Observable<DocumentAnalysis> {
    return new Observable<DocumentAnalysis>(observer => {
      // Start with a set of concurrent chunk summaries
      const chunkSummaryPromises = this.documentChunks.map((chunk, index) => 
        this.getSingleChunkSummary(chunk, index)
      );
      
      // Process all chunk summaries
      Promise.all(chunkSummaryPromises)
        .then(chunkSummaries => {
          // Combine all chunk summaries
          const combinedSummaries = chunkSummaries.join('\n\n--- Next Section Summary ---\n\n');
          
          // Create a final summary of summaries
          this.getFinalSummaryOfSummaries(combinedSummaries)
            .subscribe({
              next: finalSummary => observer.next(finalSummary),
              error: err => observer.error(err),
              complete: () => observer.complete()
            });
        })
        .catch(error => {
          console.error('Error in recursive summary generation:', error);
          observer.error(new Error('Failed to generate recursive summary: ' + error.message));
        });
    });
  }
  
  /**
   * Get a summary for a single document chunk
   */
  private async getSingleChunkSummary(chunk: string, chunkIndex: number): Promise<string> {
    // Create enhanced messages for summarizing a single chunk
    const enhancedMessages = [
        { 
          role: 'user', 
        content: 'Summarize the following section of a document in about 250 words. Focus on main points and key details only. This is section ' + 
          (chunkIndex + 1) + ' of a larger document.'
      },
      {
        role: 'assistant',
        content: 'I will summarize this section concisely, focusing on main points and key details.'
      },
      {
        role: 'user',
        content: chunk
      }
    ];
    
    // Use current model (primary or fallback)
    const currentModel = this.currentModelIndex === 0 ? this.modelId : this.fallbackModels[this.currentModelIndex - 1];
    
    try {
      // Create the request payload
      const payload = {
        model: currentModel,
        messages: enhancedMessages,
        temperature: 0.3,
        max_tokens: 500,
        stream: false
      };
      
      console.log(`Summarizing chunk ${chunkIndex + 1} with model: ${currentModel}`);
      console.log(`Chunk length: ${chunk.length} characters`);
      
      const response: any = await this.http.post(
        `${this.openRouterApiUrl}/chat/completions`, 
        payload, 
        { headers: this.headers }
      ).toPromise();
      
      console.log(`Chunk ${chunkIndex + 1} summary response:`, response);
      
      // Handle different response formats based on the model
      let content = '';
      
      if (!response) {
        throw new Error('Empty response from API');
      }
      
      if (response.choices && response.choices.length > 0) {
        const choice = response.choices[0];
        
        if (choice.message && choice.message.content) {
          // Standard format for most models
          content = choice.message.content.trim();
        } else if (choice.text) {
          // Alternative format for some models
          content = choice.text.trim();
        } else if (choice.content) {
          // Another alternative format
          content = choice.content.trim();
        } else {
          console.error('Unexpected choice structure:', choice);
          throw new Error('Could not find content in API response');
        }
      } else if (response.message && response.message.content) {
        // Direct message format
        content = response.message.content.trim();
      } else if (response.content) {
        // Simple content format
        content = response.content.trim();
      } else if (response.generated_text) {
        // Format used by some models
        content = response.generated_text.trim();
      } else {
        console.error('Unrecognized response structure:', response);
        throw new Error('Unrecognized response format from the chunk summarization API');
      }
      
      if (!content) {
        throw new Error('Empty content in API response');
      }
      
      return content;
    } catch (error) {
      console.error(`Error summarizing chunk ${chunkIndex}:`, error);
      return `[Failed to summarize section ${chunkIndex + 1}. Error: ${error.message || 'Unknown error'}]`;
    }
  }
  
  /**
   * Get a final summary based on all the individual chunk summaries
   */
  private getFinalSummaryOfSummaries(combinedSummaries: string): Observable<DocumentAnalysis> {
    // Create enhanced messages for the final summary
    const enhancedMessages = [
        { 
          role: 'system', 
        content: 'You are an expert document analyst. Create a concise final summary that captures the main ideas of the entire document.'
        },
        { 
          role: 'user', 
        content: `Create a final summary based on these section summaries:\n\n${combinedSummaries}`
      }
    ];
    
    // Use current model (primary or fallback)
    const currentModel = this.currentModelIndex === 0 ? this.modelId : this.fallbackModels[this.currentModelIndex - 1];
    
    return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, {
      model: currentModel,
      messages: enhancedMessages,
      temperature: 0.2,
      max_tokens: 600
    }, { headers: this.headers })
    .pipe(
      map(response => {
        // Reset model index if successful
        this.currentModelIndex = 0;
        
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from API');
        }
        
        return {
          type: 'summary' as const,
          content: this.formatResponseContent(response.choices[0].message.content.trim())
        };
      }),
      catchError(error => {
        console.error('Error generating final summary:', error);
        
        // Try fallback model if available
        if (this.currentModelIndex < this.fallbackModels.length) {
          console.log(`Trying fallback model ${this.currentModelIndex + 1} of ${this.fallbackModels.length}`);
          this.currentModelIndex++;
          return this.getFinalSummaryOfSummaries(combinedSummaries);
        }
        
        // Reset model index if all fallbacks failed
        this.currentModelIndex = 0;
        
        throw new Error(`Failed to generate summary: ${error.message || 'Unknown error'}`);
      })
    );
  }

  getBulletPoints(context: string): Observable<DocumentAnalysis> {
    // Create an enhanced message list with explicit instructions
    const enhancedMessages = [];
    
    // Add initial instruction as a user message
    enhancedMessages.push({
      role: 'user',
          content: 'You are an expert document analyst specializing in extracting key points from complex documents. Your task is to analyze the provided document and identify the most important points, presenting them in a clear, concise bullet-point format. Each point should capture a significant idea or piece of information from the document. Ensure the points are comprehensive, covering all major aspects of the document, while being concise and easy to understand.' 
    });
    
    // Add assistant acknowledgment
    enhancedMessages.push({
      role: 'assistant',
      content: 'I understand. I\'ll analyze the document and extract the most important points, presenting them in a clear, concise bullet-point format that covers all major aspects of the document.'
    });
    
    // Add the actual document content as a user message
    enhancedMessages.push({
          role: 'user', 
          content: `Extract the most important points from this document and present them as bullet points. Each point should be clear, concise, and capture a significant idea or piece of information:\n\n${context}` 
    });
    
    // Use current model (primary or fallback)
    const currentModel = this.currentModelIndex === 0 ? this.modelId : this.fallbackModels[this.currentModelIndex - 1];
    
    return this.http.post(`${this.openRouterApiUrl}/chat/completions`, {
      model: currentModel,
      messages: enhancedMessages,
      temperature: 0.3,
      max_tokens: 800
    }, { headers: this.headers })
    .pipe(
      map((response: any) => {
        // Reset model index if successful
        this.currentModelIndex = 0;
        
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from the bullet points API');
        }
        
        return {
          type: 'bullet-points' as const,
          content: this.formatResponseContent(response.choices[0].message.content.trim())
        };
      }),
      catchError(error => {
        console.error('Error generating bullet points:', error);
        
        // Try fallback model if available
        if (this.currentModelIndex < this.fallbackModels.length) {
          console.log(`Trying fallback model ${this.currentModelIndex + 1} of ${this.fallbackModels.length} for bullet points`);
          this.currentModelIndex++;
          return this.getBulletPoints(context);
        }
        
        // Reset model index if all fallbacks failed
        this.currentModelIndex = 0;
        
        if (error.error && error.error.error) {
          throw new Error(`Failed to generate bullet points: ${error.error.error.message || error.error.error || 'Unknown error'}`);
        } else {
        throw new Error(`Failed to generate bullet points: ${error.message || 'Unknown error'}`);
        }
      })
    );
  }

  /**
   * Ask a question about a document and get a specific answer
   */
  askQuestionAboutDocument(context: string, question: string, previousMessages: {role: string, content: string}[] = []): Observable<DocumentAnalysis> {
    console.log(`Processing document question: ${question}`);
    
    // First check if context is empty
    if (!context || context.trim() === '') {
      return of({
        type: 'answer',
        content: 'No document has been uploaded. Please upload a document first before asking questions about it.'
      });
    }
    
    // First, check if this is a general knowledge question
    return from(this.isGeneralKnowledgeQuestion(question, context)).pipe(
      switchMap(isGeneralQuestion => {
        if (isGeneralQuestion) {
          console.log('Detected as general knowledge question. Handling accordingly.');
          return this.handleGeneralKnowledgeQuestion(context, question, previousMessages, true);
        } else {
          // Check if we need to find relevant context
          if (context.length > 15000) {  // If context is quite large
            console.log('Document is large. Finding relevant sections...');
            
            // Find the most relevant context for the question
      return from(this.findRelevantContext(context, question)).pipe(
        switchMap(relevantContext => {
                console.log(`Found relevant context (${relevantContext.length} chars). Performing QA...`);
                return this.performQuestionAnswering(relevantContext, question, previousMessages);
        })
      );
    } else {
            // For smaller documents, use the complete context
            console.log('Using complete document context for answering.');
            return this.performQuestionAnswering(context, question, previousMessages);
          }
        }
      })
    );
  }

  /**
   * Determine if a question requires general knowledge beyond the document
   * @param question The user's question
   * @param context The document context
   * @returns Promise resolving to true if it's a general knowledge question, false otherwise
   */
  private async isGeneralKnowledgeQuestion(question: string, context: string): Promise<boolean> {
    // First, detect explicit requests for general knowledge
    const explicitGeneralPatterns = [
      /outside (of )?this document/i,
      /beyond (the )?document/i,
      /in general/i,
      /generally speaking/i,
      /from your (own )?knowledge/i,
      /your understanding/i,
      /what (else )?do you know about/i,
      /tell me about .+ in general/i,
      /ignore the document/i,
      /not (in|from) (the )?document/i
    ];
    
    // If explicitly requesting general knowledge, handle as such
    if (explicitGeneralPatterns.some(pattern => pattern.test(question))) {
      console.log('Explicit general knowledge question detected.');
      return true;
    }

    // Extract key terms from question to check relevance to document
    const keyTerms = this.extractKeyTerms(question);
    if (keyTerms.length === 0) {
      // If no meaningful terms, check if it's a common document-related question
      const commonDocQuestions = [
        /what (is|does) (this|the) document/i,
        /summarize/i,
        /main points/i,
        /key (points|findings)/i,
        /tell me about (this|the) document/i,
        /what (are|is) the (main|key|important)/i,
        /overview/i,
        /content/i,
        /describe/i
      ];
      
      // If it matches common document questions, treat as document-specific
      if (commonDocQuestions.some(pattern => pattern.test(question))) {
        return false;
      }
      return true;
    }
    
    // Check if key terms are found in the document
    let termsFoundCount = 0;
    let contextScore = 0;
    
    // Calculate weighted relevance score
    for (const term of keyTerms) {
      const termLower = term.toLowerCase();
      const contextLower = context.toLowerCase();
      
      // Check for exact matches
      if (contextLower.includes(termLower)) {
        termsFoundCount++;
        contextScore += 1;
      }
      
      // Check for partial matches (e.g., plurals, word stems)
      const partialMatches = contextLower.split(/\W+/).filter(word => 
        word.includes(termLower) || termLower.includes(word)
      ).length;
      
      if (partialMatches > 0) {
        contextScore += 0.5 * partialMatches;
      }
    }
    
    // Calculate final relevance score
    const relevanceScore = (termsFoundCount / keyTerms.length) + (contextScore / (2 * keyTerms.length));
    
    // Lower threshold to 0.2 and consider context score
    if (relevanceScore > 0.2) {
      console.log(`Document relevance score: ${relevanceScore.toFixed(2)}. Treating as document-specific.`);
      return false;
    }
    
    console.log(`Low document relevance score: ${relevanceScore.toFixed(2)}. Treating as general knowledge.`);
    return true;
  }

  /**
   * Enhanced semantic search to find the most relevant context for a question
   * Uses document hierarchical structure and semantic similarity
   */
  private async findRelevantContext(fullContext: string, question: string): Promise<string> {
    try {
      if (!this.documentChunks || this.documentChunks.length === 0) {
        // If no processed chunks are available, use basic chunking
        const basicChunks = this.splitIntoChunks(fullContext);
      
        if (basicChunks.length <= 1) {
        return fullContext; // No need for semantic search if there's only one chunk
      }
      
        // Continue with basic semantic search
        return this.basicSemanticSearch(basicChunks, question);
      }
      
      // If we have processed document chunks with structural information, use enhanced approach
      return this.enhancedSemanticSearch(question);
    } catch (error) {
      console.error('Error in semantic search:', error);
      return fullContext; // Fall back to the full context
    }
  }

  /**
   * Basic semantic search for when we don't have document structure
   */
  private async basicSemanticSearch(chunks: string[], question: string): Promise<string> {
      // Encode the question and all chunks
      const questionEncoding = await this.sentenceEncoder.embed([question]);
      const chunkEncodings = await this.sentenceEncoder.embed(chunks);
      
      // Calculate similarity scores
      const scores = chunks.map((_, i) => {
        // Calculate cosine similarity between question and chunk
        const similarity = this.cosineSimilarity(
        Array.from(questionEncoding.dataSync()) as number[],
        Array.from(chunkEncodings.slice([i, 0], [1, -1]).dataSync()) as number[]
        );
        return { index: i, score: similarity };
      });
      
      // Sort by similarity score (descending)
      scores.sort((a, b) => b.score - a.score);
      
      // Take the top 3 most relevant chunks
      const topChunks = scores.slice(0, 3).map(s => chunks[s.index]);
      
      // Join the top chunks to form the relevant context
      return topChunks.join('\n\n');
  }

  /**
   * Basic chunking method that splits text into paragraph-based chunks
   */
  private splitIntoChunks(text: string, maxChunkSize: number = 1000): string[] {
    // Split by paragraphs first
    const paragraphs = text.split(/\n\s*\n/);
    
    const chunks: string[] = [];
    let currentChunk = '';
    
    for (const paragraph of paragraphs) {
      if (currentChunk.length + paragraph.length > maxChunkSize && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = paragraph;
      } else {
        currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
      }
    }
    
    if (currentChunk) {
      chunks.push(currentChunk);
    }
    
    return chunks;
  }

  /**
   * Enhanced semantic search leveraging document structure and better embeddings
   */
  private async enhancedSemanticSearch(question: string): Promise<string> {
    try {
      // Check if question embedding is already cached
      let questionVector: number[];
      const cacheKey = this.createCacheKey(question);
      
      if (this.queryEmbeddingsCache.has(cacheKey)) {
        questionVector = this.queryEmbeddingsCache.get(cacheKey)!;
      } else {
        // Create and normalize the query embedding
        const questionEncoding = await this.sentenceEncoder.embed([question]);
        questionVector = this.normalizeVector(
          Array.from(questionEncoding.dataSync()) as number[]
        );
        
        // Cache the query embedding
        this.queryEmbeddingsCache.set(cacheKey, questionVector);
        
        // Limit cache size to prevent memory issues
        if (this.queryEmbeddingsCache.size > 100) {
          // Remove oldest entries (first 20)
          const keys = Array.from(this.queryEmbeddingsCache.keys()).slice(0, 20);
          keys.forEach(key => this.queryEmbeddingsCache.delete(key));
        }
      }
      
      // Create embeddings for all document chunks if not already cached
      await this.ensureDocumentEmbeddings();
      
      // Get similarity scores for each chunk with advanced ranking
      const scoredChunks = await this.rankChunksWithAdvancedScoring(questionVector, question);
      
      // Get the top chunks
      const numChunks = Math.min(3, scoredChunks.length);
      const selectedChunks = scoredChunks.slice(0, numChunks).map(c => c.chunk);
      
      // Combine the selected chunks
      return selectedChunks.join('\n\n');
    } catch (error) {
      console.error('Error in enhanced semantic search:', error);
      
      // Fallback to using most recently added chunks
      const fallbackChunks = this.documentChunks.slice(0, Math.min(2, this.documentChunks.length));
      return fallbackChunks.join('\n\n');
    }
  }

  /**
   * Ensure all document chunks have embeddings cached
   */
  private async ensureDocumentEmbeddings(): Promise<void> {
    // Find chunks that don't have cached embeddings
    const chunksToEmbed: string[] = [];
    const chunkIndices: number[] = [];
    
    this.documentChunks.forEach((chunk, index) => {
      const cacheKey = this.createCacheKey(chunk);
      if (!this.documentEmbeddingsCache.has(cacheKey)) {
        chunksToEmbed.push(chunk);
        chunkIndices.push(index);
      }
    });
    
    if (chunksToEmbed.length > 0) {
      // Embed chunks in batches to avoid OOM errors
      const batchSize = 5;
      for (let i = 0; i < chunksToEmbed.length; i += batchSize) {
        const batchChunks = chunksToEmbed.slice(i, i + batchSize);
        const batchIndices = chunkIndices.slice(i, i + batchSize);
        
        const chunkEncodings = await this.sentenceEncoder.embed(batchChunks);
        
        // Cache each chunk's embedding
        batchChunks.forEach((chunk, j) => {
          const vector = this.normalizeVector(
            Array.from(chunkEncodings.slice([j, 0], [1, -1]).dataSync()) as number[]
          );
          const cacheKey = this.createCacheKey(chunk);
          this.documentEmbeddingsCache.set(cacheKey, vector);
        });
      }
    }
  }

  /**
   * Create cache key for a text string
   */
  private createCacheKey(text: string): string {
    // Use the first and last 50 chars plus length as the key
    const prefix = text.substring(0, 50);
    const suffix = text.length > 50 ? text.substring(text.length - 50) : '';
    return `${prefix}|${text.length}|${suffix}`;
  }

  /**
   * L2 normalize a vector (unit length)
   */
  private normalizeVector(vector: number[]): number[] {
    const norm = Math.sqrt(vector.reduce((sum, val) => sum + val * val, 0));
    return vector.map(val => val / norm);
  }

  /**
   * Advanced chunk ranking with multiple scoring factors
   */
  private async rankChunksWithAdvancedScoring(questionVector: number[], question: string): Promise<any[]> {
    // Get keywords from question
    const keywords = this.extractKeywords(question);
    
    // Score each chunk
    const scoredChunks = this.documentChunks.map((chunk, i) => {
      // Get the cached embedding
      const cacheKey = this.createCacheKey(chunk);
      const chunkVector = this.documentEmbeddingsCache.get(cacheKey)!;
      
      // Calculate base semantic similarity using cosine similarity
      const semanticSimilarity = this.cosineSimilarity(questionVector, chunkVector);
      
      // Calculate keyword presence score
      const keywordScore = this.calculateWeightedKeywordScore(chunk, keywords);
      
      // Section matching
      let sectionScore = 0;
      
      // Check if there's a section mentioned in the question
      const sectionMatch = question.match(/(?:about|in|the|regarding|for|from|on)\s+(?:the)?\s*([a-zA-Z]+(?:\s+[a-zA-Z]+)?)(?:\s+section)/i);
      if (sectionMatch) {
        const sectionName = sectionMatch[1].toLowerCase();
        if (chunk.toLowerCase().includes(sectionName)) {
          sectionScore = 0.3;
        }
      }
      
      // Calculate structure bonus for chunk
      let structureBonus = 0;
      
      // Favor chunks with document context headers (structured information)
      if (chunk.includes('DOCUMENT CONTEXT:')) {
        // Extract section information
        const contextLine = chunk.split('\n')[0];
        const sections = contextLine.substring(contextLine.indexOf(':') + 1).trim().split('>');
        
        // Add bonus for each section title that contains a keyword
        for (const section of sections) {
          for (const keyword of keywords) {
            if (section.toLowerCase().includes(keyword.toLowerCase())) {
              structureBonus += 0.1; // Small bonus for each keyword match
            }
          }
        }
      }
      
      // Detect headings in the chunk (markdown or document structure)
      const hasHeadings = /\n#+ .+/g.test(chunk);
      if (hasHeadings) {
        structureBonus += 0.15; // Bonus for chunks with headings
      }
      
      // Combine all scores with appropriate weights
      // Semantic similarity is the primary factor, but other signals help break ties
      const finalScore = (semanticSimilarity * 0.7) + 
                         (keywordScore * 0.15) + 
                         (sectionScore * 0.1) + 
                         (structureBonus * 0.05);
      
      return { 
        index: i, 
        semanticSimilarity,
        keywordScore,
        sectionScore,
        structureBonus,
        score: finalScore,
        chunk
      };
    });
    
    // Sort by combined score (descending)
    return scoredChunks.sort((a, b) => b.score - a.score);
  }

  /**
   * Calculate keyword presence score for chunk with weighted scoring for multiple occurrences
   */
  private calculateWeightedKeywordScore(chunk: string, keywords: string[]): number {
    if (keywords.length === 0) return 0;
    
    const chunkLower = chunk.toLowerCase();
    let matches = 0;
    
    // Count keyword matches
    for (const keyword of keywords) {
      // Use regex to find whole word matches
      const regex = new RegExp(`\\b${keyword.toLowerCase()}\\b`, 'g');
      const occurrences = (chunkLower.match(regex) || []).length;
      if (occurrences > 0) {
        // Give more weight to multiple occurrences, but with diminishing returns
        matches += Math.min(1 + Math.log(occurrences) / Math.log(10), 2);
      }
    }
    
    // Normalize by number of keywords
    return matches / keywords.length;
  }

  /**
   * Improved cosine similarity calculation with safeguards
   */
  private cosineSimilarity(a: number[], b: number[]): number {
    if (a.length !== b.length) {
      console.error('Vector dimensions do not match:', a.length, b.length);
      return 0;
    }
    
    let dotProduct = 0;
    let normA = 0;
    let normB = 0;
    
    for (let i = 0; i < a.length; i++) {
      dotProduct += a[i] * b[i];
      normA += a[i] * a[i];
      normB += b[i] * b[i];
    }
    
    // Avoid division by zero
    normA = Math.sqrt(normA) || 1e-10;
    normB = Math.sqrt(normB) || 1e-10;
    
    return dotProduct / (normA * normB);
  }

  private performQuestionAnswering(context: string, question: string, previousMessages: {role: string, content: string}[] = []): Observable<DocumentAnalysis> {
    console.log(`Performing QA on context length ${context.length} with question: ${question.substring(0, 100)}...`);
    
    // Check if this is a quick action request
    const isQuickAction = question.includes('[Request ID:') || 
                          question.includes('[QUICK_ACTION:');
    
    // Process the question to extract special instructions
    let enhancedQuestion = question;
    let formatInstructions = '';
    
    // If this appears to be a formatting request (table, list, etc.), 
    // extract those instructions to pass separately
    if (!isQuickAction) {
      formatInstructions = this.extractFormatInstructions(question);
      if (formatInstructions) {
        enhancedQuestion = question.replace(formatInstructions, '').trim();
        console.log('Format instructions extracted:', formatInstructions);
      }
    }
    
    // Prepare the messages
    const messages = [];
    
    // Add the system message with enhanced instructions for completeness
    messages.push({
      role: 'system',
      content: `${this.systemPromptForDocument}

Additional Instructions:
1. Provide complete, well-rounded answers that fully address the question
2. Include relevant supporting details and context
3. Ensure responses are thorough but concise
4. If analyzing specific sections, include brief context about where the information comes from
5. For complex topics, break down the answer into clear parts
6. When uncertain about details, indicate what is known with certainty`
    });
    
    // Include previous conversation history (for follow-up questions)
    if (previousMessages && previousMessages.length > 0 && !isQuickAction) {
      const recentMessages = previousMessages.slice(-4);
      messages.push(...recentMessages);
    }
    
    // Smart truncation of context based on the question
    const truncatedContext = this.smartTruncateContext(context, enhancedQuestion, 20000); // Increased from 15000
    
    // Add the main document context with instructions
    messages.push({
      role: 'user',
      content: `I'm providing you with a document to analyze. Here is the document content:

${truncatedContext}

Please analyze this document carefully and provide complete, well-structured answers.`
    });
    
    // Add assistant acknowledgment
    messages.push({
      role: 'assistant',
      content: 'I have analyzed the document and I am ready to answer questions about it.'
    });
    
    // Add the question with any format instructions
    let questionMessage = enhancedQuestion;
    if (formatInstructions) {
      questionMessage += '\n\n' + formatInstructions;
    }
    
    messages.push({
      role: 'user',
      content: questionMessage
    });
    
    // Use current model (primary or fallback)
    const currentModel = this.currentModelIndex === 0 ? this.modelId : this.fallbackModels[this.currentModelIndex - 1];
    
    // Adjust token limits based on content type
    const max_tokens = isQuickAction ? 1200 : // Increased from 1000
                      formatInstructions ? 1500 : // Increased for formatted responses
                      1000; // Default increased from 800
    
    // Adjust temperature based on question type
    const temperature = isQuickAction ? 0.2 : 
                       question.toLowerCase().includes('creative') ? 0.8 :
                       0.7;
    
    return this.http.post(`${this.openRouterApiUrl}/chat/completions`, {
      model: currentModel,
      messages: messages,
      temperature: temperature,
      max_tokens: max_tokens
    }, { headers: this.headers })
    .pipe(
      map((response: any) => {
        this.currentModelIndex = 0;
        
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from QA API');
        }
        
        let content = response.choices[0].message.content.trim();
        
        // Check if response appears incomplete
        if (this.responseAppearsTruncated(content)) {
          console.log('Response appears truncated, attempting to complete it...');
          content = this.completePartialResponse(content);
        }
        
        return {
          type: 'answer' as 'summary' | 'bullet-points' | 'answer',
          content: this.formatResponseContent(content)
        };
      }),
      catchError(error => {
        console.error('Error in QA:', error);
        
        if (this.currentModelIndex < this.fallbackModels.length) {
          console.log(`Trying fallback model ${this.currentModelIndex + 1} of ${this.fallbackModels.length}`);
          this.currentModelIndex++;
          return this.performQuestionAnswering(context, question, previousMessages);
        }
        
        this.currentModelIndex = 0;
        throw new Error(`Failed to perform QA: ${error.message || 'Unknown error'}`);
      })
    );
  }

  private responseAppearsTruncated(content: string): boolean {
    // Check for common signs of truncation
    const truncationSigns = [
        /[^.!?]\s*$/,  // Ends without proper punctuation
        /\.\.\.\s*$/,  // Ends with ellipsis
        /[:,]\s*$/,    // Ends with colon or comma
        /^.*\(.*[^)]\s*$/,  // Unclosed parenthesis
        /\|\s*$/,      // Table formatting cut off
        /[-–—]\s*$/    // Cut off list or dash
    ];
    
    return truncationSigns.some(pattern => pattern.test(content));
  }

  private completePartialResponse(content: string): string {
    // Add closure to common truncation patterns
    if (/\|\s*$/.test(content)) {
        // Truncated table
        return content + '\n(Table may be truncated)';
    }
    
    if (/[-–—]\s*$/.test(content)) {
        // Truncated list
        return content + '\n(List may be incomplete)';
    }
    
    if (/[^.!?]\s*$/.test(content)) {
        // General truncation
        return content + '...';
    }
    
    return content;
  }

  /**
   * Extracts document metadata like authors, dates, etc. directly from the document
   * @param context The document content
   * @param metadataType The type of metadata to extract (author, date, etc.)
   * @returns The extracted metadata or empty string if not found
   */
  private extractDocumentMetadata(context: string, metadataType: 'author' | 'date' | 'title'): string {
    if (metadataType === 'author') {
      // Common patterns for author information
      const authorPatterns = [
        /(?:Author|AUTHORS|AUTHOR)(?:s)?(?:\s*:|\s*-|\s*>)?\s*([^\.|\n|;]{3,60})/i,
        /(?:By|BY)(?:\s*:|\s*-|\s*>)?\s*([^\.|\n|;]{3,60})/i,
        /(?:Presented by|PRESENTED BY|Submitted by|SUBMITTED BY)(?:\s*:|\s*-|\s*>)?\s*([^\.|\n|;]{3,60})/i,
        /(?:Prepared by|PREPARED BY)(?:\s*:|\s*-|\s*>)?\s*([^\.|\n|;]{3,60})/i,
        /([A-Z][a-z]+(?: [A-Z][a-z]+){1,3})(?:\s*,\s*|\s+)(?:author|presenter|submitter|writer)/i
      ];
      
      // Check each pattern
      for (const patternTemplate of authorPatterns) {
        const pattern = new RegExp(patternTemplate);
        const match = pattern.exec(context);
        if (match && match[1] && match[1].trim()) {
          return match[1].trim();
        }
      }
      
      // Look in the first 1000 characters for names that might be authors
      const firstPart = context.substring(0, 1000);
      const namePattern = /([A-Z][a-z]+(?: [A-Z][a-z]+){1,2})/g;
      let match;
      const namePattern2 = new RegExp(namePattern);
      while ((match = namePattern2.exec(firstPart)) !== null) {
        // Check if this name appears near words that suggest authorship
        const surroundingText = firstPart.substring(
          Math.max(0, match.index - 50),
          Math.min(firstPart.length, match.index + match[0].length + 50)
        );
        
        if (/author|present|submit|prepare|create|write|by|thesis/i.test(surroundingText)) {
          return match[0];
        }
      }
    }
    
    // No metadata found
    return '';
  }

  /**
   * Smart truncation that preserves the most relevant parts of the context for the question
   */
  private smartTruncateContext(context: string, question: string, maxChars: number): string {
    // If context is already short enough, return it as is
    if (context.length <= maxChars) {
      return context;
    }
    
    // Simple keyword matching to identify potentially relevant sections
    const keywords = question.toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
      .split(' ')
      .filter(word => word.length > 3); // Only consider words longer than 3 chars
    
    // Split context into paragraphs
    const paragraphs = context.split(/\n\s*\n/);
    
    // Score paragraphs based on keyword matches
    const scoredParagraphs = paragraphs.map(para => {
      const paraLower = para.toLowerCase();
      let score = 0;
      
      // Score based on keyword matches
      keywords.forEach(keyword => {
        if (paraLower.includes(keyword)) {
          score += 10;
        }
      });
      
      // Bonus for paragraphs that appear earlier (introduction often contains important context)
      const index = paragraphs.indexOf(para);
      score += Math.max(20 - index, 0);
      
      return { text: para, score };
    });
    
    // Sort paragraphs by score (highest first)
    scoredParagraphs.sort((a, b) => b.score - a.score);
    
    // Always include the first paragraph (often contains document context)
    if (!scoredParagraphs.some(p => p.text === paragraphs[0])) {
      scoredParagraphs.unshift({ text: paragraphs[0], score: 100 });
    }
    
    // Build truncated context from highest-scoring paragraphs
    let truncatedContext = '';
    let currentLength = 0;
    
    for (const para of scoredParagraphs) {
      const paraWithNewlines = para.text + '\n\n';
      if (currentLength + paraWithNewlines.length <= maxChars) {
        truncatedContext += paraWithNewlines;
        currentLength += paraWithNewlines.length;
          } else {
        // If we can't fit the entire paragraph, see if we can fit part of it
        const remainingSpace = maxChars - currentLength;
        if (remainingSpace > 100) { // Only add partial paragraph if we can fit at least 100 chars
          truncatedContext += para.text.substring(0, remainingSpace - 3) + '...\n\n';
        }
        break;
      }
    }
    
    return truncatedContext.trim();
  }

  /**
   * Format response content for better display
   * Similar to the formatResponseForDisplay method in the component
   */
  private formatResponseContent(text: string): string {
    // Convert markdown to HTML if needed
    let formatted = text;
    
    // Handle tables specially - before other formatting
    if (formatted.includes('|') && (formatted.includes('---') || formatted.includes('-|-'))) {
      // Split content by lines
      const lines = formatted.split('\n');
      let inTable = false;
      let tableHtml = '<div class="table-responsive"><table class="ai-table">';
      let headerProcessed = false;
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Detect table rows (they contain | characters)
        if (line.includes('|')) {
          if (!inTable) {
            inTable = true;
          }
          
          // Check if this is a header separator line
          if (line.match(/^\|?\s*[\-:]+[\s\-:|]+$/)) {
            headerProcessed = true;
            continue; // Skip the separator line
          }
          
          // Split the line into cells, removing empty cells at start/end if the line has | at the beginning/end
          const cells = line.startsWith('|') ? line.substring(1) : line;
          const cleanCells = cells.endsWith('|') ? cells.substring(0, cells.length - 1) : cells;
          const cellArray = cleanCells.split('|');
          
          // Create row with cells
          const isHeader = !headerProcessed;
          
          tableHtml += '<tr' + (isHeader ? ' class="table-header"' : '') + '>';
          cellArray.forEach(cell => {
            if (isHeader) {
              tableHtml += `<th>${cell.trim()}</th>`;
            } else {
              tableHtml += `<td>${cell.trim()}</td>`;
            }
          });
          tableHtml += '</tr>';
        } else if (inTable && line.trim() === '') {
          // End of table
          inTable = false;
          tableHtml += '</table></div>';
          
          // Replace this table section in the original text
          let tableStart = i;
          while (tableStart > 0 && lines[tableStart - 1].trim() !== '') {
            tableStart--;
          }
          
          // Find table end (current position or next non-empty line)
          const tableEnd = i;
          
          // Replace the table in the original text
          const beforeTable = lines.slice(0, tableStart).join('\n');
          const afterTable = lines.slice(tableEnd + 1).join('\n');
          
          return this.formatNonTableContent(beforeTable) + tableHtml + this.formatNonTableContent(afterTable);
        }
      }
      
      // If we're still in a table at the end of the text
      if (inTable) {
        tableHtml += '</table></div>';
        let tableStart = lines.length - 1;
        while (tableStart > 0 && lines[tableStart - 1].trim() !== '') {
          tableStart--;
        }
        
        const beforeTable = lines.slice(0, tableStart).join('\n');
        return this.formatNonTableContent(beforeTable) + tableHtml;
      }
    }
    
    // If no table was found or processed, format the text normally
    return this.formatNonTableContent(formatted);
  }
  
  /**
   * Format text content that is not part of a table
   */
  private formatNonTableContent(text: string): string {
    if (!text || text.trim() === '') return '';
    
    let formatted = text;
    
    // Remove excessive blank lines (more than 2 consecutive)
    formatted = formatted.replace(/\n{3,}/g, '\n\n');
    
    // Remove leading and trailing whitespace
    formatted = formatted.trim();
    
    // Detect and format lists to ensure consistent styling
    const hasBulletPoints = /^[•\-\*]\s+/m.test(formatted);
    
    if (hasBulletPoints) {
      // Format different types of bullet points consistently
      formatted = formatted.replace(/^[*]\s+(.*?)$/gm, '• $1');
      formatted = formatted.replace(/^[-]\s+(.*?)$/gm, '• $1');
      
      // Add special styling for bullet points
      formatted = formatted.replace(/^•\s*(.*?)$/gm, '<span class="bullet-point">•</span> $1');
    }
    
    // Ensure line breaks are preserved
    formatted = formatted.replace(/\n/g, '<br>');
    
    // Make markdown elements more readable
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>'); // Bold
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>'); // Italic
    formatted = formatted.replace(/__(.*?)__/g, '<strong>$1</strong>'); // Bold with underscores
    formatted = formatted.replace(/_(.*?)_/g, '<em>$1</em>'); // Italic with underscores
    
    // Format headers
    formatted = formatted.replace(/^# (.*?)$/gm, '<h1>$1</h1>');
    formatted = formatted.replace(/^## (.*?)$/gm, '<h2>$1</h2>');
    formatted = formatted.replace(/^### (.*?)$/gm, '<h3>$1</h3>');
    
    // Format code blocks - use a function to handle multiline matches instead of 's' flag
    formatted = formatted.replace(/```([\s\S]*?)```/g, function(match, codeContent) {
      return '<pre><code>' + codeContent + '</code></pre>';
    });
    formatted = formatted.replace(/`(.*?)`/g, '<code>$1</code>');
    
    // Ensure proper spacing
    formatted = formatted.replace(/<br><br><br>/g, '<br><br>');
    
    return formatted;
  }

  /**
   * Simple hash function for chunk identification
   */
  private simpleHash(text: string): string {
    let hash = 0;
    for (let i = 0; i < text.length; i++) {
      const char = text.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return hash.toString();
  }

  /**
   * Get the most relevant chunks for a question using semantic search
   * @param question The user's question
   * @param maxChunks Maximum number of chunks to return
   * @returns Most relevant chunks for the question
   */
  async getRelevantChunksForQuestion(question: string, maxChunks: number = 5): Promise<string> {
    if (!this.documentChunks.length) {
      return this.fullDocumentContent;
    }
    
    try {
      // If semantic encoder isn't loaded, use improved fallback
      if (!this.sentenceEncoder) {
        return this.getRelevantChunksFallback(question, maxChunks);
      }
      
      // Encode the question and all chunks
      const questionEncoding = await this.sentenceEncoder.embed([question]);
      const chunkEncodings = await this.sentenceEncoder.embed(this.documentChunks);
      
      // Convert TensorFlow tensors to regular arrays
      const questionVector = Array.from(questionEncoding.dataSync()) as number[];
      
      // Calculate similarity scores with improved weighting
      const scores = await this.calculateChunkScores(questionVector, chunkEncodings, question);
      
      // Sort by similarity score (descending)
      scores.sort((a, b) => b.score - a.score);
      
      // Take the top N most relevant chunks with context
      const selectedChunks = this.selectChunksWithContext(scores, maxChunks);
      
      // Join the chunks with improved context preservation
      const combinedContext = this.combineChunksWithContext(selectedChunks);
      
      return this.trimToMaxSize(combinedContext, 15000);
    } catch (error) {
      console.error('Error in semantic chunk search:', error);
      return this.getRelevantChunksFallback(question, maxChunks);
    }
  }

  /**
   * Calculate chunk scores with improved weighting
   */
  private async calculateChunkScores(questionVector: number[], chunkEncodings: any, question: string): Promise<Array<{index: number, score: number}>> {
      const scores = [];
      const numChunks = this.documentChunks.length;
    const encodingSize = questionVector.length;
      
      for (let i = 0; i < numChunks; i++) {
        const startIdx = i * encodingSize;
        const endIdx = startIdx + encodingSize;
        const chunkVector = Array.from(chunkEncodings.dataSync().slice(startIdx, endIdx)) as number[];
        
      // Calculate base similarity score
        const similarity = this.cosineSimilarity(questionVector, chunkVector);
      
      // Apply additional weighting factors
      let score = similarity;
      
      // Weight by keyword matches
      const keywords = question.toLowerCase().split(/\s+/);
      const keywordScore = this.calculateWeightedKeywordScore(this.documentChunks[i], keywords);
      score += keywordScore * 0.2;
      
      // Weight by section importance
      const sectionScore = this.calculateSectionScore(this.documentChunks[i]);
      score += sectionScore * 0.1;
      
      scores.push({ index: i, score });
    }
    
    return scores;
  }

  /**
   * Calculate simple keyword-based similarity score between question and chunk
   */
  private calculateSimpleKeywordScore(question: string, chunk: string): number {
    const questionWords = new Set(question.toLowerCase().split(/\s+/));
    const chunkWords = chunk.toLowerCase().split(/\s+/);
    
    let matches = 0;
    for (const word of chunkWords) {
      if (questionWords.has(word)) {
        matches++;
      }
    }
    
    return matches / Math.max(questionWords.size, 1);
  }

  /**
   * Calculate section importance score
   */
  private calculateSectionScore(chunk: string): number {
    // Higher score for chunks that contain section headers
    if (chunk.includes('DOCUMENT CONTEXT:')) {
      return 1.0;
    }
    
    // Medium score for chunks with metadata
    if (chunk.includes('Section Metadata:')) {
      return 0.7;
    }
    
    // Lower score for regular content
    return 0.3;
  }

  /**
   * Select chunks with context preservation
   */
  private selectChunksWithContext(scores: Array<{index: number, score: number}>, maxChunks: number): string[] {
    const selectedChunks: string[] = [];
    const selectedIndices = new Set<number>();
    
    // First, select the top scoring chunks
    for (const score of scores) {
      if (selectedChunks.length >= maxChunks) break;
      
      selectedChunks.push(this.documentChunks[score.index]);
      selectedIndices.add(score.index);
    }
    
    // Then, add context chunks if needed
    for (let i = 0; i < selectedChunks.length; i++) {
      const chunk = selectedChunks[i];
      if (chunk.includes('DOCUMENT CONTEXT:')) {
        // Find related chunks that should be included for context
        const relatedChunks = this.findRelatedChunks(scores[i].index, selectedIndices);
        for (const relatedChunk of relatedChunks) {
          if (selectedChunks.length < maxChunks + 2) { // Allow a couple extra chunks for context
            selectedChunks.push(this.documentChunks[relatedChunk]);
            selectedIndices.add(relatedChunk);
          }
        }
      }
    }
    
    return selectedChunks;
  }

  /**
   * Find chunks related to the given chunk index
   */
  private findRelatedChunks(chunkIndex: number, selectedIndices: Set<number>): number[] {
    const related: number[] = [];
    const chunk = this.documentChunks[chunkIndex];
    
    // Look for chunks that share section context
    if (chunk.includes('DOCUMENT CONTEXT:')) {
      const contextMatch = chunk.match(/DOCUMENT CONTEXT: (.*?)\n/);
      if (contextMatch) {
        const sectionPath = contextMatch[1];
        
        // Find chunks with the same section path
        for (let i = 0; i < this.documentChunks.length; i++) {
          if (i !== chunkIndex && !selectedIndices.has(i)) {
            const otherChunk = this.documentChunks[i];
            if (otherChunk.includes(`DOCUMENT CONTEXT: ${sectionPath}`)) {
              related.push(i);
            }
          }
        }
      }
    }
    
    return related;
  }

  /**
   * Combine chunks with improved context preservation
   */
  private combineChunksWithContext(chunks: string[]): string {
    let combined = '';
    let currentSection = '';
    
    for (const chunk of chunks) {
      // Extract section context if present
      const contextMatch = chunk.match(/DOCUMENT CONTEXT: (.*?)\n/);
      if (contextMatch) {
        const sectionPath = contextMatch[1];
        if (sectionPath !== currentSection) {
          // New section, add a clear separator
          if (combined) {
            combined += '\n\n=== New Section ===\n\n';
          }
          currentSection = sectionPath;
        }
      }
      
      // Add the chunk with its context
      combined += chunk + '\n\n';
    }
    
    return combined.trim();
  }

  /**
   * Fallback method for getting relevant chunks
   */
  private getRelevantChunksFallback(question: string, maxChunks: number): string {
    // Use keyword matching to find relevant chunks
    const questionWords = new Set(question.toLowerCase().split(/\s+/));
    const chunkScores = this.documentChunks.map((chunk, index) => {
      const chunkWords = chunk.toLowerCase().split(/\s+/);
      let matches = 0;
      for (const word of chunkWords) {
        if (questionWords.has(word)) {
          matches++;
        }
      }
      return { index, score: matches };
    });
    
    // Sort by matches
    chunkScores.sort((a, b) => b.score - a.score);
    
    // Take top chunks
    const selectedChunks = chunkScores
      .slice(0, maxChunks)
      .map(s => this.documentChunks[s.index]);
    
    return this.combineChunksWithContext(selectedChunks);
  }

  // Fallback method to create HTML content
  private createHtmlContent(fileName: string, responses: string[]): string {
    try {
      // Sanitize responses for HTML
      const sanitizedResponses = responses.map(response => 
        response
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;')
          .replace(/\n/g, '<br>')
      );
      
      return `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Document Analysis Results</title>
          <meta charset="UTF-8">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
              color: #333;
            }
            h1 { 
              text-align: center; 
              color: #333; 
              margin-bottom: 30px;
              padding-bottom: 10px;
              border-bottom: 1px solid #eee;
            }
            h2 { 
              color: #444; 
              margin-top: 30px; 
              padding-bottom: 5px;
              border-bottom: 1px solid #eee;
            }
            .content { 
              margin-left: 20px; 
            }
            .response { 
              margin-bottom: 25px; 
              padding: 15px; 
              background-color: #f9f9f9;
              border-left: 4px solid #3f51b5;
              border-radius: 4px;
            }
            .response-title {
              font-weight: bold;
              margin-bottom: 10px;
              color: #3f51b5;
            }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-size: 12px;
              color: #999;
            }
          </style>
        </head>
        <body>
          <h1>Document Analysis Results for ${fileName}</h1>
          
          <h2>AI ANALYSIS RESULTS</h2>
          <div class="content">
            ${sanitizedResponses.map((response, index) => 
              `<div class="response">
                <div class="response-title">Response ${index + 1}</div>
                ${response}
              </div>`
            ).join('')}
          </div>
          
          <div class="footer">
            Generated on ${new Date().toLocaleString()}
          </div>
        </body>
        </html>
      `;
    } catch (error) {
      console.error('Error creating HTML content:', error);
      // Provide a very basic fallback if even the HTML generation fails
      return `
        <!DOCTYPE html>
        <html>
        <head><title>Document Analysis</title></head>
        <body>
          <h1>Document Analysis Results</h1>
          <p>There was an error formatting the responses properly.</p>
          <pre>${JSON.stringify(responses, null, 2)}</pre>
        </body>
        </html>
      `;
    }
  }

  // Improved document text extraction
  async analyzeDocument(file: File): Promise<string> {
    try {
      const extractedText = await this.extractTextFromFile(file);
      return extractedText;
    } catch (error) {
      console.error('Error analyzing document:', error);
      throw new Error('Failed to analyze document');
    }
  }

  // Enhanced text extraction from files
  private async extractTextFromFile(file: File): Promise<string> {
    if (file.type === 'application/pdf') {
      return this.extractTextFromPdf(file);
    } else if (
      file.type === 'application/msword' || 
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return this.extractTextFromWord(file);
    } else {
      return this.extractTextGeneric(file);
    }
  }

  // Improved PDF text extraction using pdf.js
  private async extractTextFromPdf(file: File): Promise<string> {
    console.log('[AIService] Starting PDF text extraction');
    let attempts = 0;
    const maxAttempts = 3;

    while (attempts < maxAttempts) {
      attempts++;
      try {
        console.log(`[AIService] PDF extraction attempt ${attempts}/${maxAttempts}`);
        console.log('[AIService] Using PDF Worker:', pdfjsLib.GlobalWorkerOptions.workerSrc);
        
        const arrayBuffer = await this.readFileAsArrayBuffer(file);
        
        // Load the PDF document using pdf.js with explicit version handling
        const loadingTask = pdfjsLib.getDocument({
          data: new Uint8Array(arrayBuffer),
          standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`
        });
        
        // Add event listeners for better error tracking
        loadingTask.onProgress = (progress) => {
          this.ngZone.run(() => {
            console.log('Loading PDF:', Math.round(progress.loaded / progress.total * 100) + '%');
          });
        };
        
        const pdf = await loadingTask.promise;
      const numPages = pdf.numPages;
      let extractedText = '';
        let hasTextContent = false;
        let tablesExtracted = [];
        
        // First pass: Extract text content using pdf.js
      for (let i = 1; i <= numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        
          // Check if page has any text content
          if (textContent.items.length > 0) {
            hasTextContent = true;
            
            // Extract text from the page, preserving paragraph structure
            const pageText = textContent.items.reduce((text: string, item: any, index: number, items: any[]) => {
              // Add the current item's text
              text += item.str;
              
              // Check if we need to add a space or newline
              if (index + 1 < items.length) {
                const currentItem = item;
                const nextItem = items[index + 1];
                
                // If there's a significant vertical gap, add double newline for paragraph
                if (Math.abs(currentItem.transform[5] - nextItem.transform[5]) > 15) {
                  text += '\n\n';
                }
                // If items are on the same line but have a gap, add space
                else if (Math.abs(currentItem.transform[4] - nextItem.transform[4]) > 5) {
                  text += ' ';
                }
              }
              
              return text;
            }, '');
            
            extractedText += pageText + '\n\n';
            
            // Extract tables from the page
            const tables = await this.extractTablesFromPage(page);
            if (tables.length > 0) {
              tablesExtracted = [...tablesExtracted, ...tables];
            }
          }
        }
        
        // Check if we need to use OCR (if PDF has no text content)
        if (!hasTextContent || extractedText.trim().length < 100) {
          this.ngZone.run(() => {
            console.log('PDF appears to be scanned or has minimal text content. Using OCR...');
          });
          extractedText = await this.extractTextWithOCR(file);
        }
        
        // Add extracted tables to the text
        if (tablesExtracted.length > 0) {
          extractedText += '\n\nEXTRACTED TABLES:\n\n';
          tablesExtracted.forEach((table, index) => {
            extractedText += `Table ${index + 1}:\n${table}\n\n`;
          });
      }
      
      // Clean up the extracted text
        return extractedText
          .replace(/\n{3,}/g, '\n\n')  // Replace multiple newlines with double newline
          .replace(/\s+/g, ' ')        // Replace multiple spaces with single space
          .replace(/\n +/g, '\n')      // Remove spaces at start of lines
          .replace(/[^\S\n]+$/gm, '')  // Remove spaces at end of lines
          .trim();
          
    } catch (error) {
        this.ngZone.run(() => {
          console.error(`Error extracting text from PDF (attempt ${attempts}/${maxAttempts}):`, error);
        });
        
        if (attempts === maxAttempts - 1) {
          // If this was our last retry, try OCR as a final fallback
          try {
            this.ngZone.run(() => {
              console.log('Attempting OCR as final fallback...');
            });
            return await this.extractTextWithOCR(file);
          } catch (ocrError) {
            throw new Error(`Failed to extract text from PDF even with OCR: ${error.message}`);
          }
        }
        
        // Wait before retrying (exponential backoff)
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempts) * 1000));
        continue;
      }
    }
    
    throw new Error('Failed to extract text from PDF: Maximum retries exceeded');
  }

  // New method to extract text using OCR with tesseract.js
  private async extractTextWithOCR(file: File): Promise<string> {
    try {
      this.ngZone.run(() => {
        console.log('Starting OCR process with tesseract.js...');
      });
      
      // Get the PDF document using pdf.js
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      const loadingTask = pdfjsLib.getDocument({
        data: new Uint8Array(arrayBuffer),
        standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/standard_fonts/`
      });
      
      const pdfDocument = await loadingTask.promise;
      const numPages = pdfDocument.numPages;
      let fullText = '';
      
      // Create a tesseract worker for OCR
      const worker = await createWorker('eng');
      
      for (let i = 1; i <= Math.min(numPages, 20); i++) { // Limit to first 20 pages to avoid timeouts
        try {
          this.ngZone.run(() => {
            console.log(`Processing page ${i}/${numPages} with OCR...`);
          });
          
          // Get the page
          const page = await pdfDocument.getPage(i);
          
          // Render the page to an image
          const pngDataUrl = await this.renderPageToImage(page);
          
          // Perform OCR on the image
          const result = await worker.recognize(pngDataUrl);
          
          // Add page number and text to result
          fullText += `\n\n--- Page ${i} ---\n\n` + result.data.text;
          
        } catch (pageError) {
          this.ngZone.run(() => {
            console.error(`Error processing page ${i} with OCR:`, pageError);
          });
          fullText += `\n\n--- Page ${i} ---\n\n[OCR ERROR: Could not process this page]`;
        }
      }
      
      // Terminate worker
      await worker.terminate();
      
      this.ngZone.run(() => {
        console.log('OCR process completed.');
      });
      return fullText;
    } catch (error) {
      this.ngZone.run(() => {
        console.error('Error during OCR process:', error);
      });
      throw new Error(`OCR process failed: ${error.message}`);
    }
  }

  // Helper method to render a PDF page to an image for OCR
  private async renderPageToImage(page: any): Promise<string> {
    try {
      // Get the viewport for the page
      const viewport = page.getViewport({ scale: 1.5 }); // Higher scale for better OCR
      
      // Create a canvas element to render the page
      const canvas = document.createElement('canvas');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      
      // Render the page to the canvas
      const renderContext = {
        canvasContext: canvas.getContext('2d'),
        viewport: viewport
      };
      
      await page.render(renderContext).promise;
      
      // Convert the canvas to a data URL
      const dataUrl = canvas.toDataURL('image/png');
      
      // Return the data URL
      return dataUrl;
    } catch (error) {
      console.error('Error rendering PDF page to image:', error);
      throw error;
    }
  }

  // New method to extract tables from a PDF page
  private async extractTablesFromPage(page: any): Promise<string[]> {
    try {
      // Get page dimensions
      const { width, height } = await page.getViewport({ scale: 1.0 });
      
      // Get the operator list (contains drawing commands)
      const operatorList = await page.getOperatorList();
      
      // Find potential table structures (grid-like lines)
      const tables = this.detectTablesFromOperatorList(operatorList, width, height);
      
      // Extract text content
      const textContent = await page.getTextContent();
      
      // Map text content to table cells
      return this.mapTextToTables(tables, textContent, width, height);
    } catch (error) {
      console.error('Error extracting tables from page:', error);
      return [];
    }
  }

  // Helper method to detect tables from PDF operator list
  private detectTablesFromOperatorList(operatorList: any, width: number, height: number): any[] {
    // This is a simplified implementation - actual table detection is complex
    // For now, we're looking for grid-like structures of horizontal and vertical lines
    
    const horizontalLines = [];
    const verticalLines = [];
    
    // Extract line segments from operator list
    for (let i = 0; i < operatorList.fnArray.length; i++) {
      const op = operatorList.fnArray[i];
      const args = operatorList.argsArray[i];
      
      // Check for line drawing operations
      if (op === pdfjsLib.OPS.moveTo || op === pdfjsLib.OPS.lineTo) {
        // Extract line coordinates
        // This is simplified - actual implementation would need to track current path
        if (args && args.length >= 2) {
          const [x, y] = args;
          
          // Classify lines as horizontal or vertical
          if (Math.abs(y) < 0.5) { // Horizontal line
            horizontalLines.push({ x1: x, x2: x + width, y });
          } else if (Math.abs(x) < 0.5) { // Vertical line
            verticalLines.push({ y1: y, y2: y + height, x });
          }
        }
      }
    }
    
    // Find intersections to identify potential tables
    // This is very simplified - real implementation would use more sophisticated algorithms
    if (horizontalLines.length > 3 && verticalLines.length > 3) {
      return [{ 
        rows: horizontalLines.length - 1,
        columns: verticalLines.length - 1
      }];
    }
    
    return [];
  }

  // Helper method to map text content to detected tables
  private mapTextToTables(tables: any[], textContent: any, width: number, height: number): string[] {
    const result = [];
    
    // For each detected table
    for (const table of tables) {
      // Create a markdown table representation
      let markdownTable = '';
      
      // Add header row
      markdownTable += '| ' + Array(table.columns).fill('Column').map((col, i) => `${col} ${i+1}`).join(' | ') + ' |\n';
      
      // Add separator row
      markdownTable += '| ' + Array(table.columns).fill('---').join(' | ') + ' |\n';
      
      // Add empty data rows (in a real implementation, we would map text items to cells)
      for (let row = 0; row < table.rows; row++) {
        markdownTable += '| ' + Array(table.columns).fill('').join(' | ') + ' |\n';
      }
      
      result.push(markdownTable);
    }
    
    return result;
  }

  // Improved Word document text extraction using mammoth.js
  private async extractTextFromWord(file: File): Promise<string> {
    try {
      const arrayBuffer = await this.readFileAsArrayBuffer(file);
      
      // Extract text using mammoth.js
      const result = await mammoth.extractRawText({ arrayBuffer });
      let extractedText = result.value;
      
      // Add document metadata
      extractedText = `Document Type: Word Document\n` +
                      `Filename: ${file.name}\n\n` +
                      extractedText;
      
      // Clean up the extracted text
      const cleanedText = this.cleanExtractedText(extractedText);
      
      return cleanedText;
    } catch (error) {
      console.error('Error extracting text from Word document:', error);
      // Fall back to generic extraction
      return this.extractTextGeneric(file);
    }
  }

  // Generic text extraction for other file types
  private async extractTextGeneric(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const text = e.target?.result as string;
          // Enhanced text cleanup
          const cleanedText = this.cleanExtractedText(text);
          
          resolve(cleanedText);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  }

  // Helper method to clean extracted text
  private cleanExtractedText(text: string): string {
    return text
      .replace(/\r\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/\s{2,}/g, ' ')  // Remove extra spaces
      .replace(/\t/g, ' ')      // Replace tabs with spaces
      .trim();
  }

  // Detect document characteristics
  detectDocumentCharacteristics(content: string, file: File): DocumentCharacteristics {
    // Basic analysis of document content
    const hasMathContent = (/\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]|\$[\s\S]*?\$|\$\$[\s\S]*?\$\$/g).test(content) || 
                          (/\b(equation|theorem|lemma|proof|mathematics|calculus|algebra|probability)\b/gi).test(content);
    
    const hasCodeBlocks = (/```[\s\S]*?```|`[\s\S]*?`|<code>[\s\S]*?<\/code>|<pre>[\s\S]*?<\/pre>/g).test(content) ||
                           (/\b(function|const|let|var|def|class|import|from|return|if|else|for|while)\b/g).test(content);
    
    const isProbablyAcademic = (/\b(abstract|introduction|methodology|conclusion|references|bibliography|cited|journal|doi:|arxiv:|proceedings)\b/gi).test(content) ||
                               (/\b(table \d+|figure \d+|equation \d+|section \d+)\b/gi).test(content);
    
    // Check if OCR was used based on page markers in the content
    const wasOCRProcessed = content.includes('--- Page') && content.includes('OCR');
    
    // Check for tables by looking for common table patterns
    const containsTables = content.includes('EXTRACTED TABLES:') || 
                           (/\|\s*[-]+\s*\|/g).test(content) || // Markdown table separator row
                           (/<table>[\s\S]*?<\/table>/gi).test(content); // HTML tables
    
    // Detect document type
    let documentType = 'Document';
    if (file.name.toLowerCase().endsWith('.pdf')) {
      documentType = 'PDF';
    } else if (file.name.toLowerCase().endsWith('.docx') || file.name.toLowerCase().endsWith('.doc')) {
      documentType = 'Word Document';
    } else if (file.name.toLowerCase().endsWith('.txt')) {
      documentType = 'Text Document';
    }
    
    // Add academic document type detection
    if (isProbablyAcademic) {
      if ((/\b(paper|article|journal|research|study)\b/gi).test(content)) {
        documentType = 'Research Paper';
      } else if ((/\b(lecture|course|notes|slides)\b/gi).test(content)) {
        documentType = 'Academic Notes';
      } else if ((/\b(assignment|homework|problem set|exercise)\b/gi).test(content)) {
        documentType = 'Academic Assignment';
      }
    }
    
    // Approximate words and pages
    const wordCount = content.split(/\s+/).length;
    const pageCount = Math.ceil(wordCount / 500); // Very rough estimate
    
    // Determine extraction method
    let extractionMethod = 'Standard Text Extraction';
    if (wasOCRProcessed) {
      extractionMethod = 'OCR (Optical Character Recognition)';
    }
    
    return {
      isProbablyAcademic,
      hasMathContent,
      hasCodeBlocks,
      documentType,
      fileName: file.name,
      pageCount,
      wordCount,
      extractionMethod,
      isScannedDocument: wasOCRProcessed,
      containsTables
    };
  }

  // Count words in text
  private countWords(text: string): number {
    return text.split(/\s+/).filter(word => word.length > 0).length;
  }

  // Generic document analysis method that can handle any question
  analyzeDocumentWithQuestion(context: string, question: string): Observable<DocumentAnalysis> {
    // Create an enhanced prompt that will direct the LLM to follow specific instructions
    const enhancedPrompt = this.createEnhancedPrompt(context, question);
    
    return this.http.post(`${this.openRouterApiUrl}/chat/completions`, {
      model: this.modelId,
      messages: [
        { 
          role: 'system', 
          content: `You are a precision-oriented document analysis AI specializing in extracting insights from academic and technical documents. Your capabilities include:

1. Deep contextual comprehension: You understand complex academic and technical content including specialized terminology, mathematical expressions, and code.
2. Structured analysis: You can organize information into clear, logical structures including tables, hierarchical outlines, and comparisons.
3. Multi-format expertise: You excel at creating tables, bullet points, step-by-step guides, diagrams, and comparisons with proper formatting.
4. Context-aware summarization: You can identify and synthesize key information while preserving important nuances and technical details.
5. Academic precision: You maintain academic rigor in your analyses, preserving citations and contextual relationships.

You excel at:
- Creating perfectly formatted tables with clear headers and aligned columns
- Developing structured comparisons that highlight similarities and differences
- Extracting methodologies and approaches from research papers
- Identifying key findings, limitations, and conclusions
- Summarizing technical content while preserving important details

Your responses are:
- Direct and concise without unnecessary preamble or verbosity
- Precisely formatted according to the user's specifications
- Based strictly on information contained in the document
- Structured to maximize clarity and information density
- Free from meta-commentary about the analysis process

When creating tables or structured comparisons, you use proper markdown formatting and ensure alignment. You never apologize for limitations or claim inability to create any requested format.`
        },
        { 
          role: 'user', 
          content: enhancedPrompt
        }
      ],
      temperature: 0.2,
      max_tokens: 4000
    }, { headers: this.headers })
    .pipe(
      map((response: any) => {
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from the document analysis API');
        }
        
        return {
          type: 'answer' as const,
          content: response.choices[0].message.content.trim()
        };
      }),
      catchError(error => {
        console.error('Error analyzing document:', error);
        throw new Error(`Failed to analyze document: ${error.message || 'Unknown error'}`);
      })
    );
  }
  
  private createEnhancedPrompt(context: string, question: string): string {
    // Extract formatting instructions from the question
    const formatInstructions = this.extractFormatInstructions(question);
    const enhancedQuestion = this.enhanceQuestion(question);
    
    // Build a structured prompt that clearly communicates requirements
    return `
Document Content:
"""
${context}
"""

User Question: "${question}"

${formatInstructions}

Important Guidelines:
1. Focus on relevant information from the document content to answer the question
2. Use semantic understanding to infer connections between concepts even when not explicitly stated
3. Provide comprehensive analysis that captures nuances and subtleties in the text
4. Present information in a structured, organized manner that enhances comprehension
5. For academic content, preserve the academic integrity with proper citation of sections
6. When comparing concepts or methodologies:
   - Identify the core principles of each
   - Analyze similarities and differences systematically
   - Consider contextual factors that influence their application
   - Present in well-formatted tables with clear headers
7. For technical content, maintain precision in terminology and ensure accuracy
8. When summarizing, prioritize key findings, methodologies, and conclusions
9. When creating tables, use only ONE format (markdown) and never duplicate the same table in different formats
10. If asked about specific sections that don't exist in the document, clearly state that you can't find that section

Analyze the document content and provide a precise, well-structured response to the user's question.`;
  }
  
  private extractFormatInstructions(question: string): string {
    // Initialize default format instructions
    let formatInstructions = "Format Instructions:";
    let hasSpecificInstructions = false;
    
    // Look for numeric specifications (X points, X sentences, etc.)
    const numericMatch = question.match(/(\d+)\s+(sentences|points|paragraphs|words|bullet points|steps|examples|reasons|sections|key points|main ideas)/i);
    if (numericMatch) {
      formatInstructions += `\n- Provide exactly ${numericMatch[1]} ${numericMatch[2]}.`;
      hasSpecificInstructions = true;
    }
    
    // Look for format specifications
    const formatMatches = [
      { pattern: /bullet\s*points|bulleted\s*list/i, instruction: "Format as bullet points." },
      { pattern: /numbered\s*list|numbered\s*points/i, instruction: "Format as a numbered list." },
      { pattern: /table|tabular\s*format/i, instruction: "Format as a properly structured table with clear headers and aligned columns. Use ONLY markdown table format." },
      { pattern: /timeline|chronolog/i, instruction: "Format as a timeline or chronological sequence." },
      { pattern: /pros\s*and\s*cons|advantages\s*and\s*disadvantages/i, instruction: "Format as pros and cons or advantages and disadvantages." },
      { pattern: /step[\s-]*by[\s-]*step/i, instruction: "Format as step-by-step instructions." },
      { pattern: /summary/i, instruction: "Provide a concise summary." },
      { pattern: /compare\s*and\s*contrast|comparison/i, instruction: "Format as a detailed comparison highlighting similarities and differences in a structured format." },
      { pattern: /methodologies|approaches|methods/i, instruction: "Identify and analyze distinct methodologies/approaches, presenting them in a clear, structured format." }
    ];
    
    for (const {pattern, instruction} of formatMatches) {
      if (pattern.test(question)) {
        formatInstructions += `\n- ${instruction}`;
        hasSpecificInstructions = true;
      }
    }
    
    // Special case for tables and comparisons (due to their importance)
    if (/create\s+a\s+table|make\s+(?:a\s+)?table|comparison\s+table|compare\s+in\s+a\s+table/i.test(question)) {
      formatInstructions += "\n- Create a well-formatted table using markdown syntax with proper column headers and aligned rows.";
      formatInstructions += "\n- Structure the table logically to effectively compare the requested elements.";
      formatInstructions += "\n- IMPORTANT: Use ONLY markdown format for tables. Do not duplicate tables in different formats.";
      hasSpecificInstructions = true;
    }
    
    // Additional specific pattern matching
    if (/in\s*(\d+)\s*words\s*or\s*less/i.test(question)) {
      const match = question.match(/in\s*(\d+)\s*words\s*or\s*less/i);
      if (match && match[1]) {
        formatInstructions += `\n- Response must be ${match[1]} words or less.`;
        hasSpecificInstructions = true;
      }
    }
    
    // If no specific instructions were found
    if (!hasSpecificInstructions) {
      formatInstructions += "\n- Infer the appropriate format based on the user's question.";
      formatInstructions += "\n- Focus on directness and clarity in your response.";
      formatInstructions += "\n- Avoid unnecessary explanations or meta-commentary.";
      formatInstructions += "\n- If creating tables, use ONLY markdown format and do not duplicate them in different formats.";
    }
    
    return formatInstructions;
  }
  
  private enhanceQuestion(question: string): string {
    // This function could further analyze the question to determine intent
    // For now, we'll just return the original question
    return question;
  }

  // Video generation method
  generateVideo(
    prompt: string,
    duration: string,
    style: string,
    resolution: string,
    voiceoverScript?: string,
    musicStyle?: string
  ): Observable<any> {
    // This would be implemented with a real video generation API
    return this.http.post(`${this.openRouterApiUrl}/chat/completions`, {
      model: this.modelId,
      messages: [
        { role: 'system', content: 'You are an AI assistant that describes how a video would look based on the given parameters.' },
        { role: 'user', content: `Describe how a video would look with these parameters: Prompt: "${prompt}", Duration: ${duration}, Style: ${style}, Resolution: ${resolution}${voiceoverScript ? `, Voiceover: "${voiceoverScript}"` : ''}${musicStyle ? `, Music: ${musicStyle}` : ''}` }
      ],
      temperature: 0.7,
      max_tokens: 800
    }, { headers: this.headers })
    .pipe(
      map((response: any) => {
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from the video generation API');
        }
        
        return {
          description: response.choices[0].message.content.trim(),
          videoUrl: null // In a real implementation, this would be a URL to the generated video
        };
      })
    );
  }

  // Image generation method
  generateImage(prompt: string, style: string): Observable<any> {
    // This would be implemented with a real image generation API
    return this.http.post(`${this.openRouterApiUrl}/chat/completions`, {
      model: this.modelId,
      messages: [
        { role: 'system', content: 'You are an AI assistant that describes how an image would look based on the given parameters.' },
        { role: 'user', content: `Describe how an image would look with these parameters: Prompt: "${prompt}", Style: ${style}` }
      ],
      temperature: 0.7,
      max_tokens: 500
    }, { headers: this.headers })
    .pipe(
      map((response: any) => {
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from the image generation API');
        }
        
        return {
          description: response.choices[0].message.content.trim(),
          imageUrl: null // In a real implementation, this would be a URL to the generated image
        };
      })
    );
  }

  // Helper method to read a file as ArrayBuffer
  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Store a document and create overlapping chunks with semantic and hierarchical chunking
   * @param content Full document content
   * @param maxChunkSize Maximum size of each chunk
   * @returns Number of chunks created
   */
  storeDocumentWithChunks(content: string, maxChunkSize: number = 4000): number {
    if (!content || content.trim().length === 0) {
      console.warn('Empty document content provided to storeDocumentWithChunks');
      return 0;
    }
    
    // Store the full content for reference
    this.fullDocumentContent = content;
    
    // Detect document type and create appropriate chunks
    const documentType = this.detectDocumentType(content);
    
    // Create semantically meaningful chunks based on document structure
    this.documentChunks = this.createSemanticChunks(content, maxChunkSize, documentType);
    
    // Create a map for quick indexing
    this.chunkIndexMap.clear();
    this.documentChunks.forEach((chunk, index) => {
      // Use a simple hash of the first 100 chars as an identifier
      const chunkHash = this.simpleHash(chunk.substring(0, Math.min(100, chunk.length)));
      this.chunkIndexMap.set(chunkHash, index);
    });
    
    console.log(`Document processed into ${this.documentChunks.length} chunks with semantic structure preservation`);
    
    // Pre-load the sentence encoder for later use
    this.loadSentenceEncoder().then(() => {
      console.log('Sentence encoder loaded for document chunks');
    }).catch(error => {
      console.error('Error loading sentence encoder for document chunks:', error);
    });
    
    return this.documentChunks.length;
  }

  /**
   * Detect the type of document based on content patterns
   */
  private detectDocumentType(content: string): 'academic' | 'code' | 'markdown' | 'latex' | 'standard' {
    // Check for LaTeX patterns
    if (content.includes('\\begin{document}') || 
        content.includes('\\section') || 
        content.includes('\\chapter') ||
        content.includes('\\documentclass')) {
      return 'latex';
    }
    
    // Check for Markdown patterns
    if (content.includes('# ') || 
        content.includes('## ') || 
        content.includes('```') ||
        /\[.*\]\(.*\)/.test(content)) {
      return 'markdown';
    }
    
    // Check for code patterns
    if (content.includes('function ') || 
        content.includes('class ') || 
        content.includes('def ') ||
        content.includes('import ') ||
        /\{\s*\n.*\n\s*\}/.test(content)) {
      return 'code';
    }
    
    // Check for academic paper patterns
    if (content.includes('Abstract') &&
        (content.includes('Introduction') || content.includes('INTRODUCTION')) &&
        (content.includes('Conclusion') || content.includes('CONCLUSION')) &&
        (content.includes('References') || content.includes('REFERENCES') || content.includes('Bibliography'))) {
      return 'academic';
    }
    
    // Default to standard
    return 'standard';
  }

  /**
   * Create semantically meaningful chunks based on document structure
   */
  private createSemanticChunks(text: string, maxChunkSize: number, documentType: string): string[] {
    // First create a hierarchical representation of the document
    const hierarchy = this.createDocumentHierarchy(text, documentType);
    
    // Create chunks with improved context preservation
    const chunks = this.convertHierarchyToChunks(hierarchy, maxChunkSize, documentType);
    
    // Post-process chunks to ensure context continuity
    return this.postProcessChunks(chunks, hierarchy);
  }

  /**
   * Post-process chunks to ensure context continuity and improve semantic coherence
   */
  private postProcessChunks(chunks: string[], hierarchy: any[]): string[] {
    const processedChunks: string[] = [];
    let previousContext = '';
    
    for (let i = 0; i < chunks.length; i++) {
      let chunk = chunks[i];
      
      // Add document structure context if this is a new section
      if (chunk.includes('DOCUMENT CONTEXT:')) {
        // Extract section path from the context
        const contextMatch = chunk.match(/DOCUMENT CONTEXT: (.*?)\n/);
        if (contextMatch) {
          const sectionPath = contextMatch[1];
          
          // Find the corresponding section in the hierarchy
          const section = this.findSectionInHierarchy(hierarchy, sectionPath);
          if (section) {
            // Add section metadata and relationships
            chunk = this.enhanceChunkWithMetadata(chunk, section, hierarchy);
          }
        }
      }
      
      // Add relevant context from previous chunk if needed
      if (i > 0 && !chunk.includes('DOCUMENT CONTEXT:')) {
        const contextToAdd = this.extractRelevantContext(previousContext, chunk);
        if (contextToAdd) {
          chunk = `Previous Context: ${contextToAdd}\n\n${chunk}`;
        }
      }
      
      processedChunks.push(chunk);
      previousContext = chunk;
    }
    
    return processedChunks;
  }

  /**
   * Find a section in the document hierarchy based on its path
   */
  private findSectionInHierarchy(hierarchy: any[], sectionPath: string): any {
    const pathParts = sectionPath.split(' > ');
    
    const findSection = (elements: any[], targetPath: string[], currentPath: string[] = []): any => {
      for (const element of elements) {
        if (element.type === 'section') {
          const newPath = [...currentPath, element.title];
          if (newPath.join(' > ') === targetPath.join(' > ')) {
            return element;
          }
          const found = findSection(element.children || [], targetPath, newPath);
          if (found) return found;
        }
      }
      return null;
    };
    
    return findSection(hierarchy, pathParts);
  }

  /**
   * Enhance a chunk with section metadata and relationships
   */
  private enhanceChunkWithMetadata(chunk: string, section: any, hierarchy: any[]): string {
    let enhancedChunk = chunk;
    
    // Add section metadata
    if (section.metadata) {
      enhancedChunk = `Section Metadata: ${JSON.stringify(section.metadata)}\n\n${enhancedChunk}`;
    }
    
    // Add parent section context if available
    if (section.parent) {
      enhancedChunk = `Parent Section: ${section.parent.title}\n\n${enhancedChunk}`;
    }
    
    // Add sibling sections for context
    const siblings = this.findSiblingSections(section, hierarchy);
    if (siblings.length > 0) {
      enhancedChunk = `Related Sections: ${siblings.map(s => s.title).join(', ')}\n\n${enhancedChunk}`;
    }
    
    return enhancedChunk;
  }

  /**
   * Find sibling sections in the hierarchy
   */
  private findSiblingSections(section: any, hierarchy: any[]): any[] {
    const siblings: any[] = [];
    
    const findSiblings = (elements: any[], targetSection: any, parent: any = null): void => {
      for (const element of elements) {
        if (element.type === 'section') {
          if (element === targetSection && parent) {
            // Found our section, add its siblings
            siblings.push(...(parent.children || []).filter(s => s !== targetSection));
            return;
          }
          findSiblings(element.children || [], targetSection, element);
        }
      }
    };
    
    findSiblings(hierarchy, section);
    return siblings;
  }

  /**
   * Extract relevant context from previous chunk
   */
  private extractRelevantContext(previousChunk: string, currentChunk: string): string {
    // Extract the last few sentences from the previous chunk
    const sentences = previousChunk.split(/[.!?]+/).filter(s => s.trim());
    const lastSentences = sentences.slice(-2).join('. ');
    
    // Check if the context is relevant to the current chunk
    if (this.isContextRelevant(lastSentences, currentChunk)) {
      return lastSentences;
    }
    
    return '';
  }

  /**
   * Check if context is relevant to the current chunk
   */
  private isContextRelevant(context: string, chunk: string): boolean {
    // Simple relevance check based on shared keywords
    const contextWords = new Set(context.toLowerCase().split(/\s+/));
    const chunkWords = chunk.toLowerCase().split(/\s+/);
    
    let sharedWords = 0;
    for (const word of chunkWords) {
      if (contextWords.has(word)) {
        sharedWords++;
      }
    }
    
    // Consider context relevant if it shares enough keywords
    return sharedWords >= 2;
  }

  /**
   * Create a hierarchical representation of document structure
   */
  private createDocumentHierarchy(text: string, documentType: string): any[] {
    switch (documentType) {
      case 'latex':
        return this.createLatexHierarchy(text);
      case 'markdown':
        return this.createMarkdownHierarchy(text);
      case 'academic':
        return this.createAcademicHierarchy(text);
      case 'code':
        return this.createCodeHierarchy(text);
      default:
        return this.createStandardHierarchy(text);
    }
  }

  /**
   * Create hierarchy for LaTeX documents
   */
  private createLatexHierarchy(text: string): any[] {
    const hierarchy = [];
    
    // Regular expressions for LaTeX structure
    const sectionRegex = /\\(chapter|section|subsection|subsubsection|paragraph)\{([^}]+)\}/g;
    const environmentRegex = /\\begin\\{([^}]+)\\}([\s\\S]*?)\\end\\{\\1\\}/g;
    
    // Temporary storage for positions
    const positions = [];
    
    // Find all section headers
    let match;
    while ((match = sectionRegex.exec(text)) !== null) {
      positions.push({
        type: 'section',
        level: this.getLatexSectionLevel(match[1]),
        title: match[2],
        start: match.index,
        end: match.index + match[0].length
      });
    }
    
    // Find all environments
    while ((match = environmentRegex.exec(text)) !== null) {
      // Only include certain environments that are likely to be semantic units
      if (['abstract', 'theorem', 'proof', 'figure', 'table', 'algorithm'].includes(match[1])) {
        positions.push({
          type: 'environment',
          envType: match[1],
          start: match.index,
          end: match.index + match[0].length,
          content: match[0]
        });
      }
    }
    
    // Sort positions by start index
    positions.sort((a, b) => a.start - b.start);
    
    // Build hierarchy
    let lastSectionStart = 0;
    for (let i = 0; i < positions.length; i++) {
      const current = positions[i];
      
      // If this is a section, add the content before it as a paragraph
      if (current.type === 'section' && current.start > lastSectionStart) {
        const prevContent = text.substring(lastSectionStart, current.start).trim();
        if (prevContent) {
          hierarchy.push({
            type: 'paragraph',
            content: prevContent
          });
        }
        
        // Add the section header
        hierarchy.push({
          type: 'section',
          level: current.level,
          title: current.title,
          content: current.title
        });
        
        lastSectionStart = current.end;
      }
      
      // If this is an environment, add it as is
      if (current.type === 'environment') {
        hierarchy.push({
          type: 'environment',
          envType: current.envType,
          content: current.content
        });
      }
    }
    
    // Add any remaining content as a paragraph
    if (lastSectionStart < text.length) {
      const remainingContent = text.substring(lastSectionStart).trim();
      if (remainingContent) {
        hierarchy.push({
          type: 'paragraph',
          content: remainingContent
        });
      }
    }
    
    return hierarchy;
  }

  /**
   * Get section level for LaTeX section commands
   */
  private getLatexSectionLevel(command: string): number {
    switch (command) {
      case 'chapter': return 1;
      case 'section': return 2;
      case 'subsection': return 3;
      case 'subsubsection': return 4;
      case 'paragraph': return 5;
      default: return 6;
    }
  }

  /**
   * Create hierarchy for Markdown documents
   */
  private createMarkdownHierarchy(text: string): any[] {
    const hierarchy = [];
    
    // Split by lines
    const lines = text.split('\n');
    
    let currentSection = null;
    let currentParagraph = '';
    let inCodeBlock = false;
    
    for (const line of lines) {
      // Check for headers
      const headerMatch = line.match(/^(#+)\s+(.+)$/);
      
      if (headerMatch) {
        // Save any accumulated paragraph
        if (currentParagraph.trim()) {
          hierarchy.push({
            type: 'paragraph',
            content: currentParagraph.trim()
          });
          currentParagraph = '';
        }
        
        // Add header
        hierarchy.push({
          type: 'section',
          level: headerMatch[1].length,
          title: headerMatch[2],
          content: headerMatch[2]
        });
        
        continue;
      }
      
      // Check for code blocks
      if (line.startsWith('```')) {
        inCodeBlock = !inCodeBlock;
        
        // If starting a new code block, save any previous paragraph
        if (inCodeBlock && currentParagraph.trim()) {
          hierarchy.push({
            type: 'paragraph',
            content: currentParagraph.trim()
          });
          currentParagraph = '';
        }
        
        // Add the code fence to the current context
        currentParagraph += line + '\n';
        continue;
      }
      
      // If in code block, just add the line
      if (inCodeBlock) {
        currentParagraph += line + '\n';
        continue;
      }
      
      // Check for blank lines - these signify paragraph breaks
      if (!line.trim() && currentParagraph.trim()) {
        hierarchy.push({
          type: 'paragraph',
          content: currentParagraph.trim()
        });
        currentParagraph = '';
        continue;
      }
      
      // Add to current paragraph
      currentParagraph += line + '\n';
    }
    
    // Add any remaining paragraph
    if (currentParagraph.trim()) {
      hierarchy.push({
        type: 'paragraph',
        content: currentParagraph.trim()
      });
    }
    
    return hierarchy;
  }

  /**
   * Create hierarchy for academic papers
   */
  private createAcademicHierarchy(text: string): any[] {
    const hierarchy = [];
    
    // Pattern to detect common academic paper sections
    const sectionPatterns = [
      { name: 'Abstract', pattern: /abstract/i },
      { name: 'Introduction', pattern: /introduction/i },
      { name: 'Literature Review', pattern: /literature\s+review|related\s+work/i },
      { name: 'Methodology', pattern: /methodology|methods|experimental\s+setup/i },
      { name: 'Results', pattern: /results/i },
      { name: 'Discussion', pattern: /discussion/i },
      { name: 'Conclusion', pattern: /conclusion/i },
      { name: 'References', pattern: /references|bibliography/i }
    ];
    
    // Find potential section boundaries
    const lines = text.split('\n');
    const sectionBoundaries = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      
      // Skip if empty line
      if (!line) continue;
      
      // Check if line matches any section pattern
      for (const section of sectionPatterns) {
        if (section.pattern.test(line) && 
            (line.toUpperCase() === line || /^\d+\.\s+/.test(line) || line.length < 30)) {
          sectionBoundaries.push({
            index: i,
            name: section.name,
            line
          });
          break;
        }
      }
    }
    
    // Process sections
    for (let i = 0; i < sectionBoundaries.length; i++) {
      const currentSection = sectionBoundaries[i];
      const nextSection = sectionBoundaries[i + 1];
      
      // Add section header
      hierarchy.push({
        type: 'section',
        level: 1,
        title: currentSection.line,
        content: currentSection.line
      });
      
      // Get section content
      let sectionEnd = nextSection ? nextSection.index : lines.length;
      let sectionContent = lines.slice(currentSection.index + 1, sectionEnd).join('\n');
      
      // Split section content into paragraphs
      const paragraphs = sectionContent.split(/\n\s*\n/);
      
      for (const paragraph of paragraphs) {
        if (paragraph.trim()) {
          hierarchy.push({
            type: 'paragraph',
            content: paragraph.trim()
          });
        }
      }
    }
    
    // If no sections found, split into paragraphs
    if (hierarchy.length === 0) {
      const paragraphs = text.split(/\n\s*\n/);
      for (const paragraph of paragraphs) {
        if (paragraph.trim()) {
          hierarchy.push({
            type: 'paragraph',
            content: paragraph.trim()
          });
        }
      }
    }
    
    return hierarchy;
  }

  /**
   * Create hierarchy for code documents
   */
  private createCodeHierarchy(text: string): any[] {
    const hierarchy = [];
    
    // Split by lines
    const lines = text.split('\n');
    
    // Look for common code structural elements
    let currentBlock = '';
    let currentBlockType = 'unknown';
    let inMultiLineComment = false;
    let indentationLevel = 0;
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      
      // Skip empty lines
      if (!trimmedLine) {
        if (currentBlock) {
          currentBlock += '\n';
        }
        continue;
      }
      
      // Check for function/class declarations
      if (/^(function|class|def|public|private|protected|const|let|var|import|package)\s+/i.test(trimmedLine)) {
        // Save any accumulated block
        if (currentBlock) {
          hierarchy.push({
            type: 'code_block',
            blockType: currentBlockType,
            content: currentBlock
          });
        }
        
        // Determine the type of block
        if (/^function\s+/i.test(trimmedLine)) {
          currentBlockType = 'function';
        } else if (/^class\s+/i.test(trimmedLine)) {
          currentBlockType = 'class';
        } else if (/^(import|package)\s+/i.test(trimmedLine)) {
          currentBlockType = 'import';
        } else if (/^(const|let|var)\s+/i.test(trimmedLine)) {
          currentBlockType = 'variable';
        } else {
          currentBlockType = 'declaration';
        }
        
        currentBlock = line + '\n';
        continue;
      }
      
      // Append to current block
      currentBlock += line + '\n';
      
      // Check for block end (could be closing brace, reduced indentation, etc.)
      if (trimmedLine === '}' || trimmedLine === 'end' || trimmedLine === 'endif') {
        // Check if this is a major block end
        const leadingSpaces = line.search(/\S/);
        if (leadingSpaces === 0 && currentBlock) {
          hierarchy.push({
            type: 'code_block',
            blockType: currentBlockType,
            content: currentBlock
          });
          currentBlock = '';
          currentBlockType = 'unknown';
        }
      }
    }
    
    // Add any remaining block
    if (currentBlock) {
      hierarchy.push({
        type: 'code_block',
        blockType: currentBlockType,
        content: currentBlock
      });
    }
    
    return hierarchy;
  }

  /**
   * Create hierarchy for standard documents
   */
  private createStandardHierarchy(text: string): any[] {
    const hierarchy = [];
    
    // Split into paragraphs
    const paragraphs = text.split(/\n\s*\n/);
    
    for (const paragraph of paragraphs) {
      if (paragraph.trim()) {
        hierarchy.push({
          type: 'paragraph',
          content: paragraph.trim()
        });
      }
    }
    
    return hierarchy;
  }

  /**
   * Convert document hierarchy to optimized chunks
   */
  private convertHierarchyToChunks(hierarchy: any[], maxChunkSize: number, documentType: string): string[] {
    const chunks: string[] = [];
    let currentChunk = '';
    let currentSections: any[] = [];
    
    // Helper function to add the current chunk to the list
    const addCurrentChunk = () => {
      if (currentChunk.trim()) {
        chunks.push(currentChunk.trim());
        
        // Start new chunk with section context
        currentChunk = '';
        
        // Add section headers to provide context
        if (currentSections.length > 0) {
          // Add a context header to the new chunk
          currentChunk += "DOCUMENT CONTEXT: ";
          
          // Add section information
          currentSections.forEach((section, index) => {
            if (index > 0) currentChunk += " > ";
            currentChunk += section.title;
          });
          
          currentChunk += "\n\n";
        }
      }
    };
    
    // Process each element in the hierarchy
    for (const element of hierarchy) {
      // Add a section title to the current context
      if (element.type === 'section') {
        // Remove any deeper sections from the context
        while (currentSections.length > 0 && currentSections[currentSections.length - 1].level >= element.level) {
          currentSections.pop();
        }
        
        // Add this section to the context
        currentSections.push({
          level: element.level,
          title: element.title
        });
        
        // Add section headers with proper formatting
        const sectionPrefix = '#'.repeat(element.level) + ' ';
        const sectionHeader = sectionPrefix + element.title;
        
        // If adding this would exceed the max size, start a new chunk
        if (currentChunk.length + sectionHeader.length + 2 > maxChunkSize && currentChunk.length > 0) {
          addCurrentChunk();
        }
        
        // Add the section header to the current chunk
        currentChunk += sectionHeader + '\n\n';
      } 
      // Process paragraph or code block
      else {
        // If adding this would exceed the max size, start a new chunk
        if (currentChunk.length + element.content.length + 2 > maxChunkSize) {
          // If the current chunk is empty (this element itself exceeds max size)
          // OR if the current chunk is very small, we'll split this element
          if (currentChunk.length === 0 || currentChunk.length < maxChunkSize * 0.2) {
            // Split this large element
            const words = element.content.split(/\s+/);
            let tempContent = '';
            
            for (const word of words) {
              if (tempContent.length + word.length + 1 > maxChunkSize - currentChunk.length) {
                // Add what we have to the current chunk and start a new one
                currentChunk += tempContent;
                addCurrentChunk();
                tempContent = word + ' ';
              } else {
                tempContent += word + ' ';
              }
            }
            
            // Add any remaining content
            if (tempContent.trim()) {
              currentChunk += tempContent;
            }
          } else {
            // Element doesn't fit, but current chunk is substantial, so add current chunk and start new one
            addCurrentChunk();
            currentChunk += element.content + '\n\n';
          }
        } else {
          // Add the element to the current chunk
          currentChunk += element.content + '\n\n';
        }
      }
    }
    
    // Add final chunk if not empty
    if (currentChunk.trim()) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks;
  }

  // Create a downloadable document with the original content and selected responses
  createDownloadableDocument(originalFile: File, responses: string[]): Observable<Blob> {
    return new Observable<Blob>(observer => {
      try {
        if (!originalFile) {
          throw new Error('No file provided');
        }
        
        if (!responses || responses.length === 0) {
          throw new Error('No responses selected');
        }
        
        // Process responses to handle any problematic characters
        const processedResponses = responses.map(response => {
          // Replace any null bytes or other control characters
          return response.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, '');
        });
        
        // Create HTML as the fallback format
        try {
          // Create HTML content
          const htmlContent = this.createHtmlContent(originalFile.name, processedResponses);
          const blob = new Blob([htmlContent], { type: 'text/html' });
          observer.next(blob);
          observer.complete();
        } catch (error) {
          observer.error(new Error(`Failed to create document: ${error.message || 'Unknown error'}`));
        }
      } catch (error) {
        console.error('Error creating document:', error);
        observer.error(new Error(`Document creation failed: ${error instanceof Error ? error.message : 'Unknown error'}`));
      }
    });
  }

  /**
   * Public method to completely reset all document context and analysis state
   * Called when documents are removed or replaced
   */
  public resetDocumentContext(): void {
    console.log('Completely resetting document context and analysis state');
    
    // Clear all document data
    this.documentChunks = [];
    this.fullDocumentContent = '';
    this.chunkIndexMap.clear();
    this.documentEmbeddingsCache.clear();
    this.queryEmbeddingsCache.clear();
    
    // Reset any other state variables that might contain document-specific information
    this.currentModelIndex = 0; // Reset to primary model
  }

  /**
   * Update the document context with new processed content
   * Resets any previous document context first
   */
  updateDocumentContext(processedContent: string): void {
    // First reset all previous context
    this.resetDocumentContext();
    
    // Then process the new content
    console.log(`Updating document context with ${processedContent.length} characters of content`);
    
    // Store the document with chunks for better retrieval
    this.storeDocumentWithChunks(processedContent);
  }

  /**
   * Extract keywords from a question for better matching
   */
  private extractKeywords(question: string): string[] {
    // Remove common stop words and punctuation
    const stopWords = new Set(['a', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'with', 
                              'about', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 
                              'had', 'do', 'does', 'did', 'i', 'you', 'he', 'she', 'it', 'we', 'they', 
                              'this', 'that', 'these', 'those', 'what', 'which', 'who', 'whom', 'whose', 
                              'when', 'where', 'why', 'how']);
    
    return question.toLowerCase()
      .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '')
      .split(/\s+/)
      .filter(word => word.length > 3 && !stopWords.has(word));
  }

  /**
   * Clean up table output to remove raw markdown that might be present
   * alongside the formatted table
   */
  private cleanTableOutput(content: string): string {
    // Improved table detection - more comprehensive
    const hasTable = 
      (content.includes('|') && (content.includes('---') || content.includes('|-'))) || // Common markdown table
      /\+[-+]+\+/.test(content) || // ASCII style tables with + and - chars
      /^\s*[\w\s]+\s*\|\s*[\w\s]+\s*\|/.test(content); // Table-like content with words and pipes
    
    if (hasTable) {
      // Detect if there's an explanation of markdown syntax or duplicate table representation
      const lines = content.split('\n');
      let result = [];
      let foundTable = false;
      let skipUntilNextParagraph = false;
      let explanationPhrases = [
        'here is the table',
        'here\'s the table',
        'table in markdown format',
        'markdown table',
        'here is a table',
        'here\'s a table',
        'as requested',
        'i\'ve created',
        'i have created'
      ];
      
      // First pass: Clean up the content and remove explanations
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].toLowerCase();
        
        // Skip lines that explain the markdown syntax
        const isExplanationLine = explanationPhrases.some(phrase => line.includes(phrase.toLowerCase()));
        
        if (isExplanationLine && !foundTable) {
          // This is likely an introduction before the table - we'll skip it entirely
          // to avoid blank spaces in direct queries
          continue;
        }
        
        // Check if this is the start of a table
        if (line.includes('|') && !foundTable) {
          foundTable = true;
          result.push(lines[i]);
          continue;
        }
        
        // If we already found a table and hit an empty line, check if there's another table coming
        if (foundTable && line.trim() === '') {
          // Look ahead to see if there's another table explanation or duplicate
          const nextNonEmptyLine = lines.slice(i + 1).find(l => l.trim() !== '');
          if (nextNonEmptyLine && 
              (explanationPhrases.some(phrase => nextNonEmptyLine.toLowerCase().includes(phrase)) || 
               nextNonEmptyLine.includes('|'))) {
            // This is likely a duplicate explanation or table - skip until we hit a real paragraph
            skipUntilNextParagraph = true;
            continue;
          }
        }
        
        // If we're skipping and hit a real paragraph (not a table), stop skipping
        if (skipUntilNextParagraph && line.trim() !== '' && !line.includes('|')) {
          const isRealParagraph = line.length > 50 || line.endsWith('.') || line.endsWith('?') || line.endsWith('!');
          if (isRealParagraph) {
            skipUntilNextParagraph = false;
          } else {
            continue;
          }
        }
        
        // Include the line if we're not skipping
        if (!skipUntilNextParagraph) {
          result.push(lines[i]);
        }
      }
      
      // Remove any leading empty lines that might cause spacing issues
      while (result.length > 0 && result[0].trim() === '') {
        result.shift();
      }
      
      // Second pass: Now convert the markdown table to an HTML table with proper classes
      let htmlContent = result.join('\n');
      
      // Now convert the markdown table to HTML with proper styling classes
      if (htmlContent.includes('|')) {
        const tableRows = htmlContent.split('\n').filter(line => line.includes('|'));
        if (tableRows.length >= 2) { // Need at least a header and separator row
          // Start building the HTML table with proper classes for consistent styling
          let htmlTable = '<table class="ai-table">\n<thead>\n<tr class="table-header">\n';
          
          // Process header row
          const headerCells = tableRows[0].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
          headerCells.forEach(cell => {
            htmlTable += `<th>${cell}</th>\n`;
          });
          
          // Close header, skip separator row
          htmlTable += '</tr>\n</thead>\n<tbody>\n';
          
          // Process data rows (skip header and separator rows)
          for (let i = 2; i < tableRows.length; i++) {
            if (tableRows[i].trim() === '') continue;
            
            htmlTable += '<tr>\n';
            const dataCells = tableRows[i].split('|').map(cell => cell.trim()).filter(cell => cell !== '');
            dataCells.forEach(cell => {
              htmlTable += `<td>${cell}</td>\n`;
            });
            htmlTable += '</tr>\n';
          }
          
          // Close the table
          htmlTable += '</tbody>\n</table>';
          
          // Return the clean HTML table
          return htmlTable;
        }
      }
      
      return htmlContent;
    }
    
    // Not a table response, return as is
    return content;
  }

  /**
   * Rewrite queries to expand semantic understanding
   * This helps handle cases where users ask questions in different ways but mean the same thing
   */
  private expandQueryWithSemanticVariations(question: string, context: string): string {
    // Lowercase for easier comparison
    const lowerQuestion = question.toLowerCase();
    const lowerContext = context.toLowerCase();
    
    // Enhanced map of common question variations with more comprehensive synonyms
    const semanticVariations = [
      // Authorship questions - more comprehensive list of variations
      {
        patterns: [
          'who is the author', 'who wrote this', 'who created this', 'who made this', 
          'who is author', 'who authored', 'who is the writer', 'authored by',
          'who prepared', 'who submitted', 'who published', 'who is presenter',
          'who presented', 'the document is presented by', 'presented by', 
          'who is the presenter', 'submittor', 'who prepared this'
        ],
        variations: [
          'author', 'written by', 'created by', 'presented by', 'prepared by', 
          'compiled by', 'developed by', 'authored by', 'submitted by', 
          'contributed by', 'documented by', 'drafted by', 'composed by',
          'presenter', 'document presenter', 'document is presented by', 
          'this document was presented by', 'this document was written by',
          'this document was created by', 'by:', 'name:'
        ],
        // Add common locations in document headers where author information might be found
        locationHints: ['first page', 'header', 'title page', 'cover', 'beginning of document']
      },
      // Date/time questions
      {
        patterns: [
          'when was this', 'publication date', 'when did this come out', 'release date',
          'when was it published', 'when was it created', 'when was it written',
          'when was it submitted', 'when was it presented', 'document date'
        ],
        variations: [
          'date', 'published', 'released', 'created on', 'developed on', 'as of',
          'submitted on', 'presented on', 'date:', 'publication date:', 'on:', 'date of submission'
        ],
        locationHints: ['header', 'footer', 'first page', 'title page', 'cover']
      },
      // Topic/subject questions
      {
        patterns: [
          'what is this about', 'what is the main topic', 'subject of this document', 'main focus',
          'document topic', 'document subject', 'what topic', 'what subject', 'theme of'
        ],
        variations: [
          'topic', 'subject', 'about', 'focuses on', 'concerning', 'regarding', 'related to',
          'theme', 'title', 'headline', 'main subject', 'central theme', 'focus of', 'scope of'
        ]
      },
      // Purpose questions 
      {
        patterns: [
          'what is the purpose', 'why was this created', 'objective of this document',
          'goal of this document', 'intention of this document', 'aim of this document',
          'what does this document do', 'what is this document for'
        ],
        variations: [
          'purpose', 'objective', 'goal', 'aim', 'intention', 'designed to',
          'intended to', 'to achieve', 'to accomplish', 'mission', 'for the purpose of'
        ]
      }
    ];
    
    // Create an enhanced question with additional context for better understanding
    let enhancedQuestion = question;
    let addedNotes = false;
    
    // Look for names in the document that might be authors
    const namePatterns = [
      /(?:by|author|presented by|written by|submitted by)[:\s]+([A-Z][a-z]+(?: [A-Z][a-z]+)+)/gi,
      /([A-Z][a-z]+ [A-Z][a-z]+)(?:\s*,\s*|\s+)(?:author|presenter|submitted|prepared|wrote)/gi,
      /([A-Z][a-z]+(?: [A-Z][a-z]+)+)\s*(?:\(|\[)(?:author|presenter|submitter)(?:\)|\])/gi
    ];
    
    // Extract potential author names from the document
    let potentialAuthors = [];
    for (const patternTemplate of namePatterns) {
      // Need to create a new RegExp for each execution due to how exec works with global flag
      let pattern = new RegExp(patternTemplate);
      let match;
      
      // Reset the lastIndex property for each new regex
      pattern.lastIndex = 0;
      
      // Loop through all matches in the context
      while ((match = pattern.exec(context)) !== null) {
        if (match[1] && match[1].trim().length > 0) {
          potentialAuthors.push(match[1].trim());
        }
      }
    }
    
    // Check if the question matches any of our semantic variation patterns
    for (const variationGroup of semanticVariations) {
      if (variationGroup.patterns.some(pattern => lowerQuestion.includes(pattern))) {
        // If this is an authorship question, include potential author names found in document
        if (variationGroup.patterns.includes('who is the author') && potentialAuthors.length > 0) {
          enhancedQuestion += `\n\nNote: I found these potential names in the document that might be authors: ${potentialAuthors.join(', ')}. `;
          addedNotes = true;
        }
        
        // Add generic semantic variations
        if (!addedNotes) {
          enhancedQuestion += '\n\n';
          addedNotes = true;
        }
        
        enhancedQuestion += `Note: This question may be asking about information that appears in the document using different words. `;
        enhancedQuestion += `Also look for these related terms: ${variationGroup.variations.join(', ')}. `;
        
        // Add location hints for metadata that's often in specific parts of a document
        if (variationGroup.locationHints) {
          enhancedQuestion += `This information is often found in the ${variationGroup.locationHints.join(', ')} of a document. `;
        }
        
        // Emphasize logical connections for authorship
        if (variationGroup.patterns.includes('who is the author')) {
          enhancedQuestion += `\nIMPORTANT: The terms "author", "presenter", "prepared by", and "presented by" often refer to the same person. If someone presented, prepared, or submitted the document, they are likely the author unless explicitly stated otherwise. Names mentioned near title/header are often authors.`;
        }
        
        break;
      }
    }
    
    return enhancedQuestion;
  }

  /**
   * Extract key terms from a question for semantic analysis
   * This helps determine if a question is related to the document content
   */
  private extractKeyTerms(question: string): string[] {
    // Simple extraction of potential keywords by removing stop words
    const stopWords = new Set(['a', 'an', 'the', 'in', 'on', 'at', 'is', 'are', 'was', 'were', 
      'be', 'have', 'has', 'had', 'do', 'does', 'did', 'to', 'from', 'by', 'with',
      'about', 'against', 'between', 'into', 'through', 'during', 'before', 'after',
      'above', 'below', 'this', 'that', 'these', 'those', 'my', 'your', 'his', 'her',
      'its', 'our', 'their', 'what', 'which', 'who', 'whom', 'whose', 'when', 'why',
      'how', 'can', 'could', 'will', 'would', 'shall', 'should', 'may', 'might',
      'must', 'am', 'of', 'for', 'and', 'or', 'but', 'if', 'then', 'else', 'so']);
    
    // Split question into words, filter out stop words, and keep words longer than 3 characters
    return question.split(/\s+/)
      .map(word => word.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '').toLowerCase())
      .filter(word => word.length > 3 && !stopWords.has(word));
  }

  /**
   * Handle general knowledge questions that go beyond the document content
   * @param context Document context or excerpt (optional) 
   * @param question The user's question
   * @param previousMessages Previous conversation messages (optional)
   * @param isDocumentInterface Whether this is the document interface (true) or direct interface (false)
   * @returns Observable with the DocumentAnalysis response
   */
  private handleGeneralKnowledgeQuestion(
    context: string, 
    question: string, 
    previousMessages: {role: string, content: string}[] = [],
    isDocumentInterface: boolean = false
  ): Observable<DocumentAnalysis> {
    // Enhance the question with semantic variations
    const enhancedQuestion = this.expandQueryWithSemanticVariations(question, context || '');
    
    // Prepare the context to send to the AI
    let systemPrompt = this.systemPromptForGeneral;
    
    // Add additional assertiveness reminder
    systemPrompt += `\n\nFINAL INSTRUCTION: Be assertive and confident. Do not apologize for limitations. Provide direct, helpful answers with reasonable inferences where needed. Create comprehensive, well-structured output ready for copying into presentations or documents.`;
    
    // Detect if this is a request for a table or comparison - use stricter detection
    const isTableRequest = /create\s+(?:a\s+)?table|make\s+(?:a\s+)?table|provide\s+(?:a\s+)?table|show\s+in\s+table|display\s+as\s+table|generate\s+(?:a\s+)?table|show\s+(?:a\s+)?comparison\s+table|create\s+(?:a\s+)?chart|present\s+in\s+tabular\s+form/i.test(question);
    
    // For table requests, add specific instructions
    if (isTableRequest) {
      systemPrompt += `\n\nThe user is requesting a table. Create a well-formatted markdown table with proper headers and aligned columns. Your response should only include a brief introduction (1-2 lines) followed by the table. DO NOT explain the markdown syntax or duplicate the table in different formats.`;
    }
    
    // Prepare a focused message about both the document and the question
    let contextMessage = '';
    if (context && context.trim() && isDocumentInterface) {
      // Only include document context if this is the document interface
      // Include a very brief excerpt from the document for context
      const excerpt = context.length > 200 
        ? context.substring(0, 200) + '...' 
        : context;
      
      contextMessage = `Document excerpt: "${excerpt}"\n\n`;
    }
    
    // In direct interface, just ask the question directly without document context
    contextMessage += isDocumentInterface 
      ? `Please answer this question using your general knowledge: ${enhancedQuestion}` 
      : enhancedQuestion;
    
    // Create the array of messages for the API request
    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: contextMessage }
    ];
    
    // Add relevant chat history (limited to last 2 messages to save tokens)
    if (previousMessages && previousMessages.length > 0) {
      const recentMessages = previousMessages.slice(-2);
      recentMessages.forEach(msg => {
        messages.push(msg);
      });
    }
    
    // Make the API request
    return this.http.post<any>(`${this.openRouterApiUrl}/chat/completions`, {
      model: this.modelId,
      messages: messages,
      max_tokens: 1000, // Increased from 600
      temperature: 0.7,
      stream: false
    }, { headers: this.headers }).pipe(
      map(response => {
        if (!response || !response.choices || !response.choices.length) {
          throw new Error('Invalid response format from API');
        }
        
        const formattedContent = this.formatResponseContent(response.choices[0].message.content.trim());
        
        // Return in the DocumentAnalysis format
        return {
          type: 'answer' as const,
          content: formattedContent
        };
      }),
      catchError(error => {
        console.error('Error in general knowledge question:', error);
        throw error;
      })
    );
  }

  /**
   * Public method for components to access the formatted content
   * Ensures proper formatting of tables, lists and other structured content
   */
  public getFormattedContent(text: string): string {
    if (!text) return '';
    
    // Check for special formatting markers indicating quick action type
    const hasKeyPoints = text.toLowerCase().includes('key points') || text.match(/^\s*•/m);
    const hasSummary = text.toLowerCase().includes('executive summary');
    const hasOutline = text.match(/^\s*I\./m) || text.match(/^\s*I\s+[A-Z]/m);
    const hasPresentation = text.toLowerCase().includes('slide 1:') || text.toLowerCase().includes('title slide');
    
    // If this looks like a bullet point list, enhance bullet formatting
    if (hasKeyPoints) {
      // Enhance bullet formatting - make sure bullets are properly displayed
      let formatted = text;
      
      // Replace standard bullet points with better HTML bullets
      formatted = formatted.replace(/^•\s*(.*?)$/gm, '<span class="bullet-point">•</span> $1');
      formatted = formatted.replace(/^-\s*(.*?)$/gm, '<span class="sub-bullet">-</span> $1');
      
      // Ensure proper spacing and line breaks
      formatted = formatted.replace(/\n/g, '<br>');
      
      // Add title styling if there's a title
      const titleMatch = formatted.match(/(.*?)<br>/);
      if (titleMatch && !titleMatch[1].includes('•') && !titleMatch[1].includes('-')) {
        formatted = `<h3 class="result-title">${titleMatch[1]}</h3>` + 
                    formatted.substring(titleMatch[0].length);
      }
      
      return formatted;
    }
    // If this looks like a summary, enhance paragraph formatting
    else if (hasSummary) {
      let formatted = text;
      
      // Add title styling
      const titleMatch = formatted.match(/(.*?)\n/);
      if (titleMatch) {
        formatted = `<h3 class="result-title">${titleMatch[1]}</h3>` + 
                    formatted.substring(titleMatch[0].length);
      }
      
      // Make "Executive Summary:" bold
      formatted = formatted.replace(/Executive Summary:/g, '<strong>Executive Summary:</strong>');
      
      // Ensure proper paragraph breaks
      formatted = formatted.replace(/\n\n/g, '</p><p>');
      formatted = formatted.replace(/\n/g, ' ');
      
      // Wrap in paragraph tags if not already wrapped
      if (!formatted.startsWith('<p>')) {
        formatted = `<p>${formatted}</p>`;
      }
      
      return formatted;
    }
    // If this looks like an outline, enhance hierarchical formatting
    else if (hasOutline) {
      let formatted = text;
      
      // Format Roman numerals and section labels
      formatted = formatted.replace(/^(I{1,3}|IV|V|VI{1,3}|IX|X)\.\s*(.*?)$/gm, 
        '<div class="outline-section"><span class="section-number">$1.</span> <span class="section-title">$2</span></div>');
      
      // Format subsections
      formatted = formatted.replace(/^([A-Z])\.\s*(.*?)$/gm, 
        '<div class="outline-subsection"><span class="subsection-number">$1.</span> <span class="subsection-title">$2</span></div>');
      
      // Format sub-subsections
      formatted = formatted.replace(/^(\d+)\.\s*(.*?)$/gm, 
        '<div class="outline-subsubsection"><span class="subsubsection-number">$1.</span> <span class="subsubsection-title">$2</span></div>');
      
      // Ensure proper spacing
      formatted = formatted.replace(/\n/g, '<br>');
      
      return formatted;
    }
    // If this looks like presentation slides, enhance slide formatting
    else if (hasPresentation) {
      let formatted = text;
      
      // Format slide titles
      formatted = formatted.replace(/^Slide\s+(\d+):\s*(.*?)$/gmi, 
        '<div class="slide-header"><span class="slide-number">Slide $1:</span> <span class="slide-title">$2</span></div>');
      
      // Format bullet points within slides
      formatted = formatted.replace(/^-\s*(.*?)$/gm, '<div class="slide-bullet">• $1</div>');
      
      // Ensure proper spacing
      formatted = formatted.replace(/\n/g, '<br>');
      
      return formatted;
    }
    // For other content types, use the standard formatting
    else {
      // Clean up table formatting to ensure tables work properly
      if (text.includes('|')) {
        const cleanedText = this.cleanTableOutput(text);
        return cleanedText;
      }
      
      // For non-table content, apply regular formatting
      return this.formatNonTableContent(text);
    }
  }

  /**
   * Trim text to maximum size while preserving complete sentences
   */
  private trimToMaxSize(text: string, maxSize: number): string {
    if (text.length <= maxSize) return text;
    
    // Find a good breaking point (end of sentence)
    const endIndex = Math.min(maxSize, text.length);
    
    // Try to find a sentence end (., !, ?)
    const sentenceEnd = Math.max(
      text.lastIndexOf('. ', endIndex),
      text.lastIndexOf('! ', endIndex),
      text.lastIndexOf('? ', endIndex),
      text.lastIndexOf('\n', endIndex)
    );
    
    if (sentenceEnd > 0) {
      return text.substring(0, sentenceEnd + 1);
    }
    
    // If no good breaking point, just cut at max size
    return text.substring(0, maxSize);
  }

  private containsTable(content: string): boolean {
    // Check for markdown table patterns
    const hasMarkdownTable = /\|[^|]+\|[^|]+\|/.test(content) && 
                            /\|[\s-:]+\|[\s-:]+\|/.test(content);
    
    // Check for ASCII-art table patterns (often used for simple tables)
    const hasAsciiTable = /\+[-+]+\+\s*\n\s*\|/.test(content);
    
    // Check for HTML table tags - use [\s\S] instead of the 's' flag for cross-environment compatibility
    const hasHtmlTable = /<table[^>]*>[\s\S]*?<\/table>/.test(content);
    
    return hasMarkdownTable || hasAsciiTable || hasHtmlTable;
  }

  generateTextForDirectQuery(userMessage: string, options: { max_tokens?: number; temperature?: number; } = {}): Observable<string> {
    // Create a simple message array with just the user message
    const messages = [
      {
        role: 'user',
        content: userMessage
      }
    ];
    
    // Add system prompt for direct query
    const systemMessage = {
      role: 'system',
      content: this.systemPromptForGeneral
    };
    
    // Create enhanced message list with system prompt
    let enhancedMessages = [
      systemMessage,
      ...messages
    ];
    
    // Generate the text
    return this.generateTextWithFallback(enhancedMessages, 0, options).pipe(
      // Apply post-processing to improve formatting
      map(response => {
        // Format the response content (handle tables, bullet points, etc.)
        return this.formatResponseContent(response);
      }),
      catchError(error => {
        console.error('Error in generateTextForDirectQuery:', error);
        return of('Sorry, I was unable to generate a response. Please try again or rephrase your question.');
      })
    );
  }

  /**
   * Enhances a basic image description into a more detailed prompt for AI image generation
   * @param description The basic image description from the user
   * @returns An Observable containing the enhanced prompt with better details for image generation
   */
  enhanceImagePrompt(description: string): Observable<{enhancedPrompt: string, suggestedTitle: string, suggestedKeywords: string}> {
    // System prompt specifically designed for enhancing image generation prompts
    const systemPrompt = `You are an expert at writing high-quality prompts for AI image generation. Your job is to take a simple image description and turn it into a clear, detailed, and creative prompt that helps image models like Stable Diffusion, DALL·E, or SDXL generate beautiful, realistic, or artistic images.

When I give you a basic image idea, always respond in **JSON format** with these three fields:
1. enhancedPrompt: A well-written,  image prompt using simple, clear English. It should describe the scene, characters, background, lighting, mood, style, and perspective. Add helpful visual details that make the image better, even if they were not directly said.
2. suggestedTitle: A short, creative title (3 to 5 words) that fits the image idea.
3. suggestedKeywords: A list of useful keywords (e.g., lighting, style, perspective, mood, environment) that guide image creation.

Your enhanced prompt:
- Should keep the original meaning and intent of the idea.
- Should not exceed 50 words.
- Should use clean, respectful, and safe language.
- Should describe the image as if painting a picture with words.
- Should avoid complex or technical words so it's easy to understand.

Always focus on making the image prompt detailed enough to help the model generate high-quality output.
`;


    // Create message array
    const messages = [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: `Enhance this image description: "${description}"` }
    ];

    // Use existing method with fallback capability
    return this.generateTextWithFallback(messages, 0, {
      temperature: 0.7
    }).pipe(
      map(response => {
        try {
          // First approach: Try to extract JSON using regex for the most common pattern
          let enhancedPrompt = description; // Default to original if extraction fails
          let suggestedTitle = '';
          let suggestedKeywords = '';
          
          console.log('Raw model response:', response);
          
          // First try to find JSON object
          const jsonMatch = response.match(/\{[\s\S]*?\}/);
          
          if (jsonMatch) {
            try {
              // Try to parse as JSON first
              const jsonData = JSON.parse(jsonMatch[0]);
              if (jsonData.enhancedPrompt) {
                enhancedPrompt = jsonData.enhancedPrompt;
                suggestedTitle = jsonData.suggestedTitle || '';
                suggestedKeywords = jsonData.suggestedKeywords || '';
              }
            } catch (innerError) {
              console.log('Failed to parse extracted JSON, falling back to regex extraction');
            }
          }
          
          // If JSON parsing failed, try to extract with regex
          if (enhancedPrompt === description) {
            // Look for enhancedPrompt field
            const promptMatch = response.match(/["']?enhancedPrompt["']?\s*:[\s"']*([^"'}]+)["'\s}]/i);
            if (promptMatch && promptMatch[1]) {
              enhancedPrompt = promptMatch[1].trim();
            }
            
            // Look for title separately
            const titleMatch = response.match(/["']?suggestedTitle["']?\s*:[\s"']*([^"'}]+)["'\s}]/i);
            if (titleMatch && titleMatch[1]) {
              suggestedTitle = titleMatch[1].trim();
            }
            
            // Look for keywords separately
            const keywordsMatch = response.match(/["']?suggestedKeywords["']?\s*:[\s"']*([^"'}]+)["'\s}]/i);
            if (keywordsMatch && keywordsMatch[1]) {
              suggestedKeywords = keywordsMatch[1].trim();
            }
          }
          
          // If all extraction failed, use a more aggressive approach
          if (enhancedPrompt === description) {
            // Look for any paragraph that's longer than the original and might be the enhanced prompt
            const paragraphs = response.split('\n\n');
            for (const paragraph of paragraphs) {
              if (paragraph.length > description.length * 1.2 && 
                  !paragraph.includes(':') && 
                  !paragraph.includes('```')) {
                enhancedPrompt = paragraph.trim();
                break;
              }
            }
          }
          
          return {
            enhancedPrompt: enhancedPrompt || description,
            suggestedTitle: suggestedTitle,
            suggestedKeywords: suggestedKeywords
          };
        } catch (error) {
          console.error('Error parsing enhanced prompt response:', error);
          return {
            enhancedPrompt: description, // Fall back to original description
            suggestedTitle: '',
            suggestedKeywords: ''
          };
        }
      }),
      catchError(error => {
        console.error('Error enhancing image prompt:', error);
        return of({
          enhancedPrompt: description, // Fall back to original description
          suggestedTitle: '',
          suggestedKeywords: ''
        });
      })
    );
  }
} 